import React, { useState, useRef, useEffect } from 'react';
import {
  Check,
  Copy,
  Mic,
  X,
  ExternalLink,
  Send,
  Volume2,
  VolumeX,
  CheckCircle,
  AlertCircle,
  StopCircle,
  Download,
  User as UserIcon
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { stripAssistantMarkers } from '../utils/stripAssistantMarkers';
import remarkGfm from 'remark-gfm';
import { aiChatsApi } from '../services/aiChatsApi';
import {
  SlashCommandMenu,
  matchSlashCommand,
  type SlashCommandDef,
} from './SlashCommandMenu';
import type { AnalysisType } from '../types/aiAnalysis';
import { Button } from '@/components/ui/button';
import { useDateFormat } from '@/hooks/useDateFormat';
import { TextInput } from '@/components/ui/text-input';

/**
 * The bubble's slash-command surface. Working commands fire
 * immediately on selection; coming-soon ones surface the namespace
 * (so the user knows what's planned) and emit a friendly inline
 * message instead of failing.
 */
/**
 * The bubble's slash-command surface. Every command listed here is
 * wired up and works — no placeholder "Soon" entries. When a new
 * command is introduced, add it here AND its handler in
 * `handleSlashCommand` together; we don't want to ship a name in
 * the menu before the dispatch path exists.
 */
const SLASH_COMMANDS: SlashCommandDef[] = [
  // Analysis (fire AnalysisProcessor with a pinned type). The argHint
  // tells the user what optional focus the command accepts; backend
  // passes args through to the LLM as `USER FOCUS:` to narrow scope.
  {
    name: 'analyze',
    argHint: '[focus]',
    description: 'Clinical assessment — optional focus, e.g. /analyze post-op',
  },
  {
    name: 'risk',
    argHint: '[area]',
    description: 'Risk assessment — e.g. /risk cardiovascular · /risk falls',
  },
  {
    name: 'labs',
    argHint: '[test]',
    description: 'Interpret labs — e.g. /labs CBC · /labs A1c',
  },
  {
    name: 'meds',
    argHint: '[drug]',
    description: 'Medication safety + interactions — e.g. /meds metformin',
  },
  {
    name: 'vitals',
    argHint: '[trend]',
    description: 'Vital signs assessment — e.g. /vitals blood pressure',
  },
  {
    name: 'summary',
    argHint: '[scope]',
    description: 'Clinical summary — e.g. /summary last 30 days',
  },
  // Workflow
  { name: 'book', description: 'Schedule a follow-up appointment' },
  // Housekeeping
  { name: 'clear', description: 'Clear this conversation' },
  { name: 'new', description: 'Start a new chat' },
  { name: 'help', description: 'Show all commands' },
];

const ANALYSIS_BY_COMMAND: Record<string, AnalysisType> = {
  analyze: 'patient-clinical-assessment',
  risk: 'patient-risk-assessment',
  labs: 'lab-results-interpretation',
  meds: 'medication-safety-check',
  vitals: 'vital-signs-assessment',
  summary: 'clinical-summary-generation',
};
import { useAIStore } from '../stores/useAIStore';
import { useAIActionExecutor } from '../hooks/useAIActionExecutor';
import type { AIAction } from '../types/aiActions';
import { MaieLogomark } from './MaieLogomark';
import { ProposalCard } from './ProposalCard';
import { PatientProposalCard } from './PatientProposalCard';
import { useNavigate } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { toast } from 'sonner';
import { aiGatewayService } from '../services/aiGateway';
import { AnalysisResultCard } from './AnalysisResultCard';
import { BookingConfirmationCard } from './BookingConfirmationCard';
import { usePageContext } from '../hooks/usePageContext';
import { useWakeWordDetection } from '../hooks/useWakeWordDetection';
import {
  WakeWordOnboardingModal,
  hasSeenWakeWordOnboarding,
  isWakeWordModelCached,
} from './WakeWordOnboardingModal';
import { useVoiceStore } from '../stores/useVoiceStore';
import { useThemeStore } from '../stores/useThemeStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useAiAffordance } from '@/config/aiCapabilities';
import {
  primeTtsPlayback,
  playWavBytes,
  interSentencePauseMs,
  type TtsPlaybackHandle,
} from '../utils/ttsPlayback';

/**
 * The MAIE disc gradient — identical to the /assistant workspace's
 * AssistantAvatar so the bubble and the full-page workspace read as the
 * same product surface.
 */
const ASSISTANT_DISC_GRADIENT =
  'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700, var(--color-primary-600, var(--color-primary-500))))';

/**
 * Gradient assistant avatar disc (mirrors the workspace AssistantAvatar).
 * Breathes while the assistant is thinking/streaming so state reads at a
 * glance.
 */
const AssistantDisc: React.FC<{ active?: boolean; className?: string }> = ({
  active = false,
  className = '',
}) => (
  <div
    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
      active ? 'animate-pulse motion-reduce:animate-none' : ''
    } ${className}`}
    style={{ background: ASSISTANT_DISC_GRADIENT }}
  >
    {/* MAIE's own mark, not a robot glyph — see AssistantPage's disc. */}
    <MaieLogomark variant="symbol-beige" size={16} title="" />
  </div>
);

/** Hover-reveal copy button for assistant messages (workspace idiom). */
const CopyMessageButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      aria-label="Copy message"
      title="Copy message"
      className="rounded-md p-1.5 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 flex-shrink-0"
      style={{ color: 'var(--color-text-tertiary)' }}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5" style={{ color: 'var(--color-success)' }} />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
};

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

/**
 * Inline citation chip. Renders one of:
 *   - `[E<n>]` → clickable encounter link, tooltip shows the visit date.
 *   - `[S]`    → clickable patient-summary link, tooltip explains the source.
 *   - `[T:..]` → static tool label, tooltip shows the tool name.
 * Visually small + tinted so it reads as a citation marker rather than
 * inline content; tap target padded so it works on mobile.
 */
type CitationKind =
  | { type: 'encounter'; n: number; encounterId: string | null; visitDate?: string }
  | { type: 'summary' }
  | { type: 'tool'; tool: string }
  | { type: 'unknown'; raw: string };

interface CitationChipProps {
  kind: CitationKind;
  patientId: string | null;
  onOpenEncounter?: (encounterId: string) => void;
  onOpenPatientSummary?: (patientId: string) => void;
}

const CitationChip: React.FC<CitationChipProps> = ({
  kind,
  patientId,
  onOpenEncounter,
  onOpenPatientSummary,
}) => {
  const { formatDate } = useDateFormat();
  let label = '';
  let title = '';
  let isInteractive = false;
  let onClick: (() => void) | undefined;

  if (kind.type === 'encounter') {
    label = `E${kind.n}`;
    title = kind.visitDate
      ? `Encounter ${kind.n} — ${formatDate(kind.visitDate)}`
      : `Encounter ${kind.n}`;
    if (kind.encounterId && onOpenEncounter) {
      isInteractive = true;
      onClick = () => onOpenEncounter(kind.encounterId!);
    }
  } else if (kind.type === 'summary') {
    label = 'S';
    title = 'From the patient summary';
    if (patientId && onOpenPatientSummary) {
      isInteractive = true;
      onClick = () => onOpenPatientSummary(patientId);
    }
  } else if (kind.type === 'tool') {
    label = `T:${kind.tool}`;
    title = `From tool: ${kind.tool}`;
  } else {
    label = kind.raw;
    title = `Citation: ${kind.raw}`;
  }

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.65rem',
    lineHeight: 1,
    padding: '1px 5px',
    marginInline: '2px',
    borderRadius: '4px',
    verticalAlign: 'super',
    fontWeight: 600,
    backgroundColor:
      'color-mix(in srgb, var(--color-primary-500) 12%, transparent)',
    color: 'var(--color-primary-700)',
    border:
      '1px solid color-mix(in srgb, var(--color-primary-500) 22%, transparent)',
    cursor: isInteractive ? 'pointer' : 'default',
    userSelect: 'none',
  };

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        style={baseStyle}
      >
        {label}
      </button>
    );
  }
  return (
    <span title={title} aria-label={title} style={baseStyle}>
      {label}
    </span>
  );
};

/**
 * Matches the three citation marker shapes the patient-QA prompt
 * teaches the model to emit (see HEIDI's patient-qa.prompt.ts):
 *   - `[E<n>]` — encounter index (1-based)
 *   - `[S]`    — cached patient summary
 *   - `[T:<tool>]` — data from a tool call
 * `T:` can contain anything up to the closing bracket (tool names with
 * underscores, dots, dashes).
 */
const CITATION_MARKER_RE = /\[(E\d+|S|T:[^\]]+)\]/g;

const PROPOSE_HEAD_RE =
  /\[PROPOSE:\s*(?:lab_orders|imaging_orders|prescriptions|patient|document)\(/g;

/**
 * Strip TOOL_CALL and PROPOSE markers from a partial streaming reply so
 * the bubble never flashes the raw JSON envelope at the user. PROPOSE
 * payloads contain nested `{items:[…]}` braces, so we walk forward from
 * each head and count braces to find the matching close — a non-greedy
 * regex would stop at the first inner `}` and leave garbage behind.
 * Mid-stream markers (no matching `)]` yet) are masked from the head
 * onward; once the close arrives, the full span gets sliced out cleanly.
 */
// Remove `[TOOL_CALL: …]` markers by BRACKET BALANCE, not a literal `)]`
// closer. The model's marker format drifts (`) ]`, `}]`, newline before
// the close); a `\)\]` regex misses every drift and leaves the raw call
// in the bubble (or, with a slice-to-start fallback, blanks the answer
// after it). Balancing `[`/`]` catches the marker wherever it closes and
// keeps the prose before AND after it. A marker with no close yet is the
// streaming tail — hidden from its start.
function stripToolCallMarkers(text: string): string {
  let out = text;
  for (;;) {
    const start = out.indexOf('[TOOL_CALL:');
    if (start === -1) break;
    let depth = 0;
    let end = -1;
    for (let i = start; i < out.length; i++) {
      if (out[i] === '[') depth++;
      else if (out[i] === ']') {
        depth--;
        if (depth === 0) {
          end = i + 1;
          break;
        }
      }
    }
    if (end === -1) {
      out = out.slice(0, start);
      break;
    }
    out = out.slice(0, start) + out.slice(end);
  }
  // Strip internal ref markers the model parrots from context — bracketed
  // UUIDs, [S]/[E] spans, [T:tool] cites — so raw ids never render.
  return stripAssistantMarkers(out);
}

function stripStreamingMarkers(text: string): string {
  let out = stripToolCallMarkers(text);

  const heads: Array<{ start: number; end: number | null }> = [];
  for (const head of out.matchAll(new RegExp(PROPOSE_HEAD_RE.source, 'g'))) {
    if (head.index == null) continue;
    const jsonStart = head.index + head[0].length;
    if (out[jsonStart] !== '{') {
      // Marker still mid-stream; mask from head onward.
      heads.push({ start: head.index, end: null });
      continue;
    }
    let depth = 0;
    let inString = false;
    let escape = false;
    let jsonEnd = -1;
    for (let i = jsonStart; i < out.length; i++) {
      const ch = out[i];
      if (escape) {
        escape = false;
        continue;
      }
      if (inString) {
        if (ch === '\\') escape = true;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') inString = true;
      else if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) {
          jsonEnd = i + 1;
          break;
        }
      }
    }
    if (jsonEnd === -1 || out.slice(jsonEnd, jsonEnd + 2) !== ')]') {
      // Still mid-stream — JSON body or trailing `)]` not yet received.
      heads.push({ start: head.index, end: null });
      continue;
    }
    heads.push({ start: head.index, end: jsonEnd + 2 });
  }

  if (heads.length === 0) return out;
  // Walk right-to-left so each slice keeps earlier offsets valid.
  for (let i = heads.length - 1; i >= 0; i--) {
    const { start, end } = heads[i];
    out = end == null ? out.slice(0, start) : out.slice(0, start) + out.slice(end);
  }
  return out;
}

interface CitationContext {
  encounterIdMap?: Array<{ marker: string; encounterId: string; visitDate?: string }>;
  patientId: string | null;
  onOpenEncounter?: (encounterId: string) => void;
  onOpenPatientSummary?: (patientId: string) => void;
}

function resolveMarker(rawInside: string, ctx: CitationContext): CitationKind {
  if (rawInside === 'S') return { type: 'summary' };
  if (rawInside.startsWith('T:')) {
    return { type: 'tool', tool: rawInside.slice(2) };
  }
  const m = /^E(\d+)$/.exec(rawInside);
  if (m) {
    const n = parseInt(m[1], 10);
    const literal = `[E${n}]`;
    const entry = ctx.encounterIdMap?.find((e) => e.marker === literal);
    return {
      type: 'encounter',
      n,
      encounterId: entry?.encounterId ?? null,
      visitDate: entry?.visitDate,
    };
  }
  return { type: 'unknown', raw: `[${rawInside}]` };
}

/**
 * Walk through React children produced by react-markdown, splitting
 * every string node on the citation regex and replacing each match
 * with a <CitationChip />. Non-string nodes (elements like <strong>,
 * <em>) are left intact — we don't expect markers inside those, but
 * if a future model puts one there we'd want it rendered as a plain
 * string rather than recursively processed (keeps the typography rule
 * "chips only appear in prose" stable).
 */
function processCitationChildren(
  children: React.ReactNode,
  ctx: CitationContext,
): React.ReactNode {
  const out: React.ReactNode[] = [];
  let chipKey = 0;
  React.Children.forEach(children, (child) => {
    if (typeof child !== 'string') {
      out.push(child);
      return;
    }
    // String-based split using matchAll — same semantics as exec-in-loop
    // but plays nicer with iterator typing and avoids RegExp.lastIndex
    // bookkeeping.
    const matches = Array.from(child.matchAll(CITATION_MARKER_RE));
    if (matches.length === 0) {
      out.push(child);
      return;
    }
    let lastIdx = 0;
    for (const match of matches) {
      const idx = match.index ?? 0;
      if (idx > lastIdx) out.push(child.slice(lastIdx, idx));
      const kind = resolveMarker(match[1], ctx);
      out.push(
        <CitationChip
          key={`cite-${chipKey++}`}
          kind={kind}
          patientId={ctx.patientId}
          onOpenEncounter={ctx.onOpenEncounter}
          onOpenPatientSummary={ctx.onOpenPatientSummary}
        />,
      );
      lastIdx = idx + match[0].length;
    }
    if (lastIdx < child.length) out.push(child.slice(lastIdx));
  });
  return out;
}

/**
 * Build the `components` prop for ReactMarkdown so prose-bearing
 * elements walk their text children through the citation processor.
 * We only override the elements that typically contain marker text
 * (paragraph, list item, table cell, blockquote). Headings, code
 * spans, and links are left alone — the model doesn't put citation
 * markers there, and overriding more elements adds risk for no gain.
 */
function buildCitationMarkdownComponents(ctx: CitationContext) {
  const wrap =
    (Tag: keyof React.JSX.IntrinsicElements) =>
    ({ children, ...rest }: React.HTMLAttributes<HTMLElement>) => {
      const Comp = Tag as unknown as React.ElementType;
      return <Comp {...rest}>{processCitationChildren(children, ctx)}</Comp>;
    };
  return {
    p: wrap('p'),
    li: wrap('li'),
    td: wrap('td'),
    th: wrap('th'),
    blockquote: wrap('blockquote'),
  };
}

export const AIAssistantBubble: React.FC = () => {
  const { showAssistant, setShowAssistant, contexts, pendingPrompt, setPendingPrompt, pendingChatMessage, setPendingChatMessage, pendingAnalysisRequest, triggerAnalysis, contextChips, attachContextChip, detachContextChip, toggleContextChipEnabled, pendingVoiceMode, setPendingVoiceMode } = useAIStore();
  const pageContext = usePageContext(); // Get current page context
  const { settings: voiceSettings, updateSettings: updateVoiceSettings } =
    useVoiceStore();
  const { branding } = useThemeStore();
  const { t } = useTranslation();
  const { formatDate, formatCalendarDate } = useDateFormat();
  const { enableWakeWord, useClientSideWhisper, wakeWordMode, pushToActivateKey } = voiceSettings;

  // Workspace 'Voice mode' → open the bubble in continuous conversation
  // (same path the wake word takes: listen, answer aloud, keep the mic
  // hot during TTS so the doctor can interrupt).
  useEffect(() => {
    if (!pendingVoiceMode) return;
    setPendingVoiceMode(false);
    setShowAssistant(true);
    setIsMinimized(false);
    isContinuousModeRef.current = true;
    updateVoiceSettings({ enableTTS: true });
    setTimeout(() => {
      if (!isRecordingRef.current) startVoiceRecording();
    }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVoiceMode]);

  // AI (Ask-MAIE) affordance. The assistant runs the LLM, so when AI is
  // off-plan / out of quota / the stack is down, the whole voice path
  // (wake word → record → send) must be inert — otherwise a no-AI clinic
  // can still wake-word a recording and ship it to a disabled backend.
  // `enabled` is true only when reason === 'ok'. Pre-load it fails open
  // (STATIC_FALLBACK), matching the rest of the AI-degradation surfaces.
  const assistantAffordance = useAiAffordance('assistant');
  const assistantEnabled = assistantAffordance.enabled;
  // Keep a ref so async closures (recording stop handlers, push-to-talk)
  // read the live value without being recreated.
  const assistantEnabledRef = useRef(assistantEnabled);
  assistantEnabledRef.current = assistantEnabled;


  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  // Position represents the bottom-right corner of the window
  const [position, setPosition] = useState<Position>({
    x: window.innerWidth - 20, // x is the right edge position
    y: window.innerHeight - 20  // y is the bottom edge position
  });
  const [size, setSize] = useState<Size>({
    width: window.innerWidth < 640 ? window.innerWidth - 40 : 400,
    height: window.innerWidth < 640 ? Math.min(window.innerHeight * 0.7, 500) : 500
  });
  const [message, setMessage] = useState('');
  const [_isListening, setIsListening] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  /** Last reply stopped because it hit the model's token cap, not because
   *  it finished. Drives the Continue affordance. */
  const [lastTruncated, setLastTruncated] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [_showContextPreview, setShowContextPreview] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    actions?: AIAction[];
    analysisData?: unknown;
    patientContext?: { id: string; name: string };
    /** Inline citation resolver — populated from
     *  `assistant.chat-complete.encounterIdMap` so we can resolve
     *  `[E<n>]` markers in the text to a clickable encounter link.
     *  `[S]` and `[T:tool]` resolve client-side from constants. */
    encounterIdMap?: Array<{ marker: string; encounterId: string; visitDate?: string }>;
  }>>([]);
  const [pendingActions, setPendingActions] = useState<AIAction[]>([]);
  const [_voiceTranscript, setVoiceTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [currentPatientContext, setCurrentPatientContext] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Session ID for conversation tracking (persists across page refreshes via localStorage).
  // Legacy — server now ignores this and routes by `activeChatId` (a server-allocated
  // AiChatThread id). Kept on the wire for one rollout so older builds keep working.
  const [sessionId] = useState<string>(() => {
    const stored = localStorage.getItem('ai-session-id');
    if (stored) return stored;
    const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ai-session-id', newId);
    return newId;
  });

  // Active chat thread the bubble is bound to. `null` = let the server
  // auto-resume (per-patient or global). Server stamps the resolved id
  // on the `assistant.chat-start` event so we can switch the bubble to
  // it for subsequent turns.
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  // Set true by `/new` so the NEXT send forces a brand-new backend thread.
  // Clearing activeChatId alone isn't enough: with no chatId the server
  // falls back to ensureAutoThread, which RESUMES the latest existing
  // thread for (user, patient) — so "new chat" kept appending to the old
  // conversation and replaying its whole history (ballooning token cost).
  // The explicit newThread flag makes the server force-create instead.
  const forceNewThreadRef = useRef(false);
  // Tracks the patient id from the chip rail across renders so we can
  // detect a change and reset the active chat. Without this, attaching
  // a new patient chip while the bubble is bound to a different
  // patient's thread would route the next turn to the OLD thread
  // (chatId wins over patientId in resolveOrCreate server-side).
  const lastChipPatientIdRef = useRef<string | null>(null);
  // Slash-command menu state. The menu is open whenever the input
  // starts with `/` and isn't followed by typing past a space (so
  // `/order CBC` keeps it closed once args are being entered).
  const [slashActiveIndex, setSlashActiveIndex] = useState(0);

  // Selection and confirmation state for voice-based multi-result handling
  const [activeSelectionContext] = useState<unknown>(null);

  const bubbleRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const gatewayConnectedRef = useRef<boolean>(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const voiceSessionIdRef = useRef<string>('');
  const isStoppingRef = useRef<boolean>(false);
  const finalChunkSentRef = useRef<boolean>(false);
  const vadAudioContextRef = useRef<AudioContext | null>(null);
  const vadAnalyserRef = useRef<AnalyserNode | null>(null);
  const vadSilenceFramesRef = useRef<number>(0);
  const vadCheckIntervalRef = useRef<number | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  // TTS playback queue. Each chunk is tagged with a per-turn owner id
  // (`${chatId}#${ts}`) captured at the moment the chunk arrived from
  // the gateway. Without this tag two leaks were observed:
  //   1. Switching chat threads while a slow TTS response was still
  //      streaming leaked the old-thread audio into the new thread.
  //   2. A fast double-send on the SAME thread saw both responses'
  //      chunks interleave in the queue (the existing
  //      "clear queue on send" guard runs once, but chunks that
  //      arrive AFTER the clear still belonged to the old turn).
  // On dequeue we drop any chunk whose ownerTurnId doesn't match the
  // current audioOwnerTurnIdRef; on thread switch / on each new send
  // we bump (or clear) that id so all in-flight chunks become stale.
  const audioQueueRef = useRef<Array<{ audio: ArrayBuffer; text: string; ownerTurnId: string }>>([]);
  // Playback handle for the in-flight TTS chunk (see utils/ttsPlayback:
  // shared unlocked element + Web Audio fallback).
  const ttsHandleRef = useRef<TtsPlaybackHandle | null>(null);
  // Pending inter-sentence pause timer, cleared on any hard stop.
  const ttsPauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Call SYNCHRONOUSLY inside a click/tap/keypress handler. */
  const primeTtsAudio = (): void => {
    primeTtsPlayback();
  };
  const isPlayingAudioRef = useRef<boolean>(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  // Mirror of activeChatId so the gateway event listener (registered
  // once with an empty-deps useEffect) can read the current thread
  // without closing over a stale value.
  const activeChatIdRef = useRef<string | null>(null);
  // Owner of the currently-expected TTS stream. Empty string = no
  // active turn; any chunk arriving in that state is stale.
  const audioOwnerTurnIdRef = useRef<string>('');
  const isContinuousModeRef = useRef<boolean>(false);
  const shouldRestartRecordingRef = useRef<boolean>(false);
  const speechFramesCountRef = useRef<number>(0); // Track how many frames had actual speech
  const streamingContentRef = useRef<string>(''); // Accumulates llm.delta tokens during streaming
  const [isStreaming, setIsStreaming] = useState(false);
  // After a few seconds with no first token (cold model still loading,
  // slow network/proxy, or a heavy prompt) reassure the user instead of
  // leaving silent dots. Doesn't expose infra state — just "taking longer".
  const [slowHint, setSlowHint] = useState(false);
  useEffect(() => {
    if (!isThinking && !isStreaming) {
      setSlowHint(false);
      return;
    }
    const t = setTimeout(() => setSlowHint(true), 7000);
    return () => clearTimeout(t);
  }, [isThinking, isStreaming]);
  // Cross-device sync: another device of this user appended to the
  // bubble's pinned thread (server push via the Postgres-NOTIFY
  // fan-out; no content on the event). Refetch over REST and rebuild —
  // but only when the server actually has MORE messages than local
  // state, and never while this bubble is mid-turn: the optimistic
  // stream is ahead of the DB and a refetch would clobber it (and the
  // actions/analysis cards that exist only locally).
  const remoteSyncBusyRef = useRef(false);
  remoteSyncBusyRef.current = isThinking || isStreaming;
  useEffect(() => {
    const off = aiGatewayService.on(
      'assistant.thread-updated',
      (evt: { threadId?: string }) => {
        if (!evt?.threadId || evt.threadId !== activeChatIdRef.current) return;
        if (remoteSyncBusyRef.current) return;
        aiChatsApi
          .get(evt.threadId)
          .then((detail) => {
            if (
              activeChatIdRef.current !== evt.threadId ||
              remoteSyncBusyRef.current
            ) {
              return;
            }
            const hydrated = detail.messages
              .filter((m) => m.role === 'user' || m.role === 'assistant')
              .map((m) => {
                // Same analysis-card re-hydration as thread loading.
                const toolCall = m.toolCall as
                  | { type?: string; data?: unknown }
                  | null;
                const analysisData =
                  toolCall && toolCall.type === 'analysis'
                    ? (toolCall.data as unknown)
                    : undefined;
                return {
                  role: m.role as 'user' | 'assistant',
                  content: m.content,
                  ...(analysisData ? { analysisData } : {}),
                };
              });
            setChatMessages((prev) =>
              hydrated.length > prev.length ? hydrated : prev,
            );
          })
          .catch(() => undefined);
      },
    );
    return off;
  }, []);
  // NOTE: An auto-`chatInterrupt()` on bubble unmount was tried and
  // reverted 2026-05-08. It tripped any time the bubble unmounted —
  // React StrictMode's dev double-invoke, parent re-renders that drop
  // the component, layout reshuffles — even when no human "close"
  // happened, killing in-flight chat streams that came back blank
  // and routing the LATEST llm.delta payload (often a stale summary)
  // to whatever bubble mounted next. The user-driven Stop button
  // (search this file for `chatInterrupt`) is the right affordance;
  // unmount-time cancellation needs more careful gating before it can
  // come back. Don't reintroduce without tying it to an explicit
  // close-intent signal, not just unmount.
  const { navigateTo } = useAppNavigation();
  const navigate = useNavigate();
  const { executeActions } = useAIActionExecutor();

  // Last patient id we auto-attached as a chip suggestion. Tracked so
  // that if the user dismisses the chip on a patient page, we don't
  // immediately re-attach it on the next render — only when they
  // navigate to a *different* patient does a fresh chip appear.
  const lastAutoAttachedPatientIdRef = useRef<string | null>(null);

  // On patient-page entry, surface the patient as a removable chip
  // suggestion instead of silently injecting it into every chat turn.
  // Rationale (user feedback 2026-05-13): the previous behaviour
  // auto-included pageContext.currentPatient into context.currentPatient
  // unconditionally, which meant asking unrelated questions on a
  // patient's page still got patient-bound responses. By making the
  // chip the *only* path to patient context (see payload below), the
  // user can dismiss it for "ask anything" mode and re-attach via the
  // "Add to context" button if they change their mind.
  useEffect(() => {
    const pagePatient = pageContext.currentPatient;
    // Leaving the patient page clears the "we already suggested this
    // patient" memory — so navigating back later re-surfaces the chip
    // instead of silently respecting a long-stale dismissal.
    if (!pagePatient?.id) {
      lastAutoAttachedPatientIdRef.current = null;
      return;
    }
    if (lastAutoAttachedPatientIdRef.current === pagePatient.id) return;
    // Don't reattach if a chip for this patient is already on the rail.
    const alreadyAttached = contextChips.some(
      (c) => c.type === 'patient' && c.id === pagePatient.id,
    );
    lastAutoAttachedPatientIdRef.current = pagePatient.id;
    if (alreadyAttached) return;
    attachContextChip(
      {
        type: 'patient',
        id: pagePatient.id,
        label: pagePatient.name,
        enabled: true,
      },
      // quiet:true — auto-attach on patient-page load shouldn't pop
      // the bubble. The user opens it themselves when they want it.
      // The chip waits for them in the input rail; if they dim it
      // they can ask unrelated questions without patient context.
      { quiet: true },
    );
  }, [pageContext.currentPatient?.id, pageContext.currentPatient?.name, attachContextChip, contextChips]);

  // Keep activeChatIdRef in sync with state, and on every thread switch
  // hard-stop in-flight TTS playback so a late-arriving response from
  // the previous thread can't bleed into the new one. Without this, the
  // moment the user picked a different chat from the list, audio from
  // the prior turn (still arriving in chunks) would keep playing on top
  // of whatever the new thread surfaced.
  useEffect(() => {
    const prevChatId = activeChatIdRef.current;
    activeChatIdRef.current = activeChatId;
    if (prevChatId === activeChatId) return;
    // First send on a fresh bubble starts with activeChatId=null and
    // the server-assigned id arrives via `chat-start` mid-turn. That
    // null→real transition isn't a thread switch — the turn's TTS
    // chunks are tagged with the `pending#…` owner we stamped at send
    // time, and wiping audioOwnerTurnIdRef here would make the very
    // first chunks land as "orphan" and get dropped. The new id is
    // just the resolution of the same in-flight turn; leave the owner
    // and audio queue alone so playback proceeds normally.
    if (prevChatId === null) return;
    // Thread changed — flush any pending TTS belonging to the old thread.
    ttsHandleRef.current?.stop();
    ttsHandleRef.current = null;
    if (ttsPauseTimerRef.current) {
      clearTimeout(ttsPauseTimerRef.current);
      ttsPauseTimerRef.current = null;
    }
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = '';
      } catch {
        // pause() rejects with AbortError if play() was still pending;
        // safe to ignore — we just want it silent.
      }
      currentAudioRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingAudioRef.current = false;
    // Invalidate the current turn owner so any chunks arriving from the
    // old thread (already in flight) are dropped on enqueue / dequeue.
    audioOwnerTurnIdRef.current = '';
    // Forget the last auto-attached patient so a "new chat" or
    // thread-switch on the SAME patient page re-attaches the chip
    // on the next render. Without this, starting a fresh thread
    // while standing on a patient's page produced a chip-less first
    // turn — patientId went out as undefined and the LLM had no
    // patient context (user-reported 2026-05-13).
    lastAutoAttachedPatientIdRef.current = null;
  }, [activeChatId]);

  // Wake-word consent gate. The Whisper model is cached per-browser per-machine,
  // so a setting that says "enabled" does not imply the model is actually
  // present here — e.g. site-data was cleared, or the setting traveled in via
  // server sync on a fresh browser. We probe the Cache Storage on mount and
  // only let the worker spin up once we've confirmed the model is local AND
  // the user has seen the onboarding on this browser. Otherwise we surface
  // the onboarding modal so the on-device download is a deliberate choice.
  const [wakeWordConsented, setWakeWordConsented] = useState(false);
  const [showWakeOnboarding, setShowWakeOnboarding] = useState(false);
  // True only when this mount is starting from an empty Cache Storage —
  // i.e. there's a real network download about to happen. We need this
  // because @xenova/transformers v2 fires `progress` events on cache
  // hits too (it streams bytes out of the cached Response), so the
  // worker's progress messages alone can't distinguish "downloading"
  // from "loading from cache". Without this flag the chip would flash
  // on every refresh once the model is already cached.
  const [isFreshDownload, setIsFreshDownload] = useState(false);

  useEffect(() => {
    if (!enableWakeWord) {
      setWakeWordConsented(false);
      setShowWakeOnboarding(false);
      setIsFreshDownload(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const cached = await isWakeWordModelCached();
      if (cancelled) return;
      if (cached && hasSeenWakeWordOnboarding()) {
        setWakeWordConsented(true);
        setShowWakeOnboarding(false);
        setIsFreshDownload(false);
      } else {
        setWakeWordConsented(false);
        setShowWakeOnboarding(true);
        setIsFreshDownload(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enableWakeWord]);

  // Wake word detection
  const wakeWordState = useWakeWordDetection({
    // Don't even listen for the wake word when AI isn't usable — no point
    // running the model or arming the mic if the command can't be served.
    enabled: enableWakeWord && wakeWordConsented && assistantEnabled,
    useClientSideWhisper,
    mode: wakeWordMode,
    pushToActivateKey,

    // Callback: Wake word detected → open chat bubble and start recording
    onWakeWordDetected: () => {
      console.log('🎯 Wake word detected - opening chat bubble and starting recording');
      setShowAssistant(true); // Open the chat bubble
      setIsMinimized(false);  // Ensure it's not minimized

      // Enable continuous conversation mode
      isContinuousModeRef.current = true;
      console.log('🔄 Continuous conversation mode enabled');

      // Start voice recording to capture the full command
      if (!isRecording) {
        console.log('🎙️ Starting voice recording after wake word detection');
        startVoiceRecording();
      }
    },

    // Callback: Client-side transcription ready
    onTranscriptionReady: (text: string) => {
      console.log('📝 Client-side wake word transcription received:', text);
      // For wake word detection in client-side mode:
      // The wake word itself is detected, but we should NOT send it.
      // Instead, the onWakeWordDetected callback already started the recording
      // and we'll wait for the actual voice recording transcription from the backend.

      // Don't set message or auto-send for wake word detection
      // The backend transcription will handle the actual user command
      console.log('⏭️ Skipping auto-send for wake word - waiting for full command from recording');
    }
  });

  // When the model becomes ready DURING a fresh download:
  //   - if the modal is still mounted, let it run its 1.4s "ready" beat
  //     and clear the flag from `onReadyDismiss`. (We can't clear it here
  //     or the modal flashes back to the intro phase before the beat.)
  //   - if the user already minimized the modal to the corner chip,
  //     fire a success toast and clear the flag now so the chip hides.
  // `notifiedReadyRef` makes the toast fire exactly once per fresh
  // download, even if `isListening` flips a few times during warmup.
  const notifiedReadyRef = useRef(false);
  useEffect(() => {
    if (!isFreshDownload) {
      notifiedReadyRef.current = false;
      return;
    }
    if (!wakeWordState.isListening) return;
    if (showWakeOnboarding) return; // modal handles it
    if (notifiedReadyRef.current) return;
    notifiedReadyRef.current = true;
    toast.success('Voice model ready. Try saying “Hey MAIE, start next”.');
    setIsFreshDownload(false);
  }, [wakeWordState.isListening, isFreshDownload, showWakeOnboarding]);

  // Always show the bubble - it should be available for users to access

  // Dragging and resizing logic
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isResizing && window.innerWidth >= 640) {
        // Update position based on bottom-right corner with proper offset
        setPosition({
          x: Math.max(size.width + 20, Math.min(window.innerWidth - 20, e.clientX - dragOffset.x)),
          y: Math.max(size.height + 20, Math.min(window.innerHeight - 20, e.clientY - dragOffset.y))
        });
      } else if (isResizing && window.innerWidth >= 640) {
        // Resize from top-left handle, keeping bottom-right corner fixed
        const rect = bubbleRef.current?.getBoundingClientRect();
        if (rect) {
          // Calculate new top-left position based on mouse
          const newLeft = Math.max(0, Math.min(e.clientX, rect.right - 300)); // Ensure min width and stay in viewport
          const newTop = Math.max(0, Math.min(e.clientY - 48, rect.bottom - 200)); // -48 for header, ensure min height and stay in viewport

          // Calculate new size based on the new top-left position
          const newWidth = rect.right - newLeft;
          const newHeight = rect.bottom - newTop;

          // Ensure the window doesn't exceed viewport boundaries
          const maxWidth = window.innerWidth - newLeft - 20;
          const maxHeight = window.innerHeight - newTop - 20;

          // Update size - limited by viewport and minimum sizes
          setSize({
            width: Math.max(300, Math.min(maxWidth, newWidth)),
            height: Math.max(200, Math.min(maxHeight, newHeight))
          });

          // Since we're anchored at bottom-right, we need to update position
          // to maintain the bottom-right corner location
          setPosition({
            x: rect.right,  // Keep the same right position
            y: rect.bottom  // Keep the same bottom position
          });
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (window.innerWidth < 640) return;
      e.preventDefault();

      const touch = e.touches[0];
      if (isDragging && !isResizing) {
        // Update position based on bottom-right corner with proper offset
        setPosition({
          x: Math.max(size.width + 20, Math.min(window.innerWidth - 20, touch.clientX - dragOffset.x)),
          y: Math.max(size.height + 20, Math.min(window.innerHeight - 20, touch.clientY - dragOffset.y))
        });
      } else if (isResizing) {
        // Resize from top-left handle, keeping bottom-right corner fixed
        const rect = bubbleRef.current?.getBoundingClientRect();
        if (rect) {
          // Calculate new top-left position based on touch
          const newLeft = Math.max(0, Math.min(touch.clientX, rect.right - 300)); // Ensure min width and stay in viewport
          const newTop = Math.max(0, Math.min(touch.clientY - 48, rect.bottom - 200)); // -48 for header, ensure min height and stay in viewport

          // Calculate new size based on the new top-left position
          const newWidth = rect.right - newLeft;
          const newHeight = rect.bottom - newTop;

          // Ensure the window doesn't exceed viewport boundaries
          const maxWidth = window.innerWidth - newLeft - 20;
          const maxHeight = window.innerHeight - newTop - 20;

          // Update size - limited by viewport and minimum sizes
          setSize({
            width: Math.max(300, Math.min(maxWidth, newWidth)),
            height: Math.max(200, Math.min(maxHeight, newHeight))
          });

          // Since we're anchored at bottom-right, we need to update position
          // to maintain the bottom-right corner location
          setPosition({
            x: rect.right,  // Keep the same right position
            y: rect.bottom  // Keep the same bottom position
          });
        }
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, isResizing, position, size, dragOffset]);

  // Reset active chat when the chip is swapped from one patient to
  // ANOTHER patient — that's a genuinely new conversation. Used to
  // also reset on null transitions (chip removed / detached after a
  // turn / disabled), which produced the reported bug 2026-05-13:
  // voice turn auto-detached its chip → effect fired → activeChatId
  // wiped mid-conversation → user's follow-up text spawned a fresh
  // chat instead of continuing the same one (and the typed message
  // appeared not to send because the placeholder was discarded).
  //
  // Only an enabled chip counts as "the current patient binding" —
  // a dimmed chip means the user opted out of patient context for
  // the next turn but the conversation itself hasn't changed
  // subject. Re-enabling the same chip is a no-op.
  useEffect(() => {
    const enabledChip = contextChips.find(
      (c) => c.type === 'patient' && c.enabled !== false,
    );
    const currentPatientId = enabledChip?.id ?? null;
    const previousPatientId = lastChipPatientIdRef.current;
    if (currentPatientId === previousPatientId) return;
    // Update the ref unconditionally so we track the latest binding.
    lastChipPatientIdRef.current = currentPatientId;
    // Only the genuine patient-X → patient-Y case warrants a chat
    // reset. Transitions to null (chip detached / disabled) leave
    // the thread alone — the backend still routes by activeChatId,
    // and the user's intent here is "don't send patient this turn",
    // not "start a new conversation".
    if (previousPatientId !== null && currentPatientId !== null) {
      setActiveChatId(null);
      setChatMessages([]);
      streamingContentRef.current = '';
    }
  }, [contextChips]);

  // Auto-hide when minimized after delay.
  //
  // Race fix (2026-05-09): the original effect only watched
  // `isMinimized` and would keep its 5s timer alive after the user
  // re-opened the bubble via MaieLauncherPill. The timer fired later
  // with `setShowAssistant(false)`, hiding the freshly-opened bubble
  // for a moment until the user re-opened it again. Now we watch
  // `showAssistant` too: any re-open flips out of the minimized state
  // and the cleanup clears the pending timer.
  useEffect(() => {
    if (!isMinimized) return;
    if (showAssistant) {
      // User re-opened (launcher pill, wake word, command palette).
      // Drop the minimized state so a future minimize → reopen cycle
      // starts clean — and so the timer below isn't re-scheduled.
      setIsMinimized(false);
      return;
    }
    const timer = setTimeout(() => {
      setIsMinimized(false);
      setShowAssistant(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isMinimized, showAssistant, setShowAssistant]);

  // Show assistant when contexts change or showAssistant is set
  useEffect(() => {
    if (showAssistant && !isMinimized) {
      // Auto-expand when new prompts are sent
    }
  }, [showAssistant, contexts, isMinimized]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Handle pending prompt
  useEffect(() => {
    if (pendingPrompt) {
      setMessage(pendingPrompt);
      // Focus the input field
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [pendingPrompt]);

  // Handle pending analysis requests via /ai-assistant socket (uses AnalysisProcessor with DB enrichment)
  useEffect(() => {
    if (pendingAnalysisRequest) {
      console.log('📤 Sending analysis request via ai-assistant socket:', pendingAnalysisRequest);

      // Push the user "Analyze: ..." line + an EMPTY assistant placeholder
      // in the same setState so the typing-dots renderer (gated on the
      // last message being an empty assistant + isThinking) actually
      // fires. Without the placeholder the bubble showed the user's
      // request and nothing else for the full 10–15s the analysis took.
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: `Analyze: ${(pendingAnalysisRequest as { analysisType: string }).analysisType.replace(/-/g, ' ')}`,
        },
        {
          role: 'assistant',
          content: '',
        },
      ]);

      setIsThinking(true);

      // Helper: replace the trailing empty-assistant placeholder with
      // the real response (or an error fallback). Avoids tail-pushing
      // a new assistant message that would leave the placeholder
      // dangling above it.
      const settleAssistantPlaceholder = (
        update: { content: string; analysisData?: unknown },
      ) => {
        setChatMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            const m = next[i];
            if (m.role === 'assistant' && m.content === '') {
              next[i] = { ...m, ...update };
              return next;
            }
          }
          // No placeholder found (shouldn't happen) — fall back to
          // appending so the message isn't lost.
          next.push({ role: 'assistant', ...update });
          return next;
        });
      };

      // Send via the AI Gateway singleton → AnalysisProcessor.
      // Backend persists the analysis result to the active chat
      // thread (creates one via patient auto-resume if no chatId is
      // supplied) and returns the resolved id so we can pin
      // `activeChatId` and have the card survive a chat-list switch.
      if (aiGatewayService.isConnected()) {
        const requestWithChatId = {
          ...(pendingAnalysisRequest as Record<string, unknown>),
          chatId: activeChatId ?? undefined,
        };
        aiGatewayService.assistantAnalysis(requestWithChatId).then((data) => {
          console.log('🔬 AI analysis response received:', data);

          setIsThinking(false);

          if (data.context?.currentPatient) {
            console.log('📝 Updating patient context from analysis:', data.context.currentPatient);
            setCurrentPatientContext(data.context.currentPatient);
          }

          if (data?.chatId && data.chatId !== activeChatId) {
            setActiveChatId(data.chatId);
          }

          settleAssistantPlaceholder({
            content: data.summary,
            analysisData: data,
          });
        }).catch((err) => {
          console.error('❌ Analysis request failed:', err);
          setIsThinking(false);
          settleAssistantPlaceholder({
            content: 'Sorry, the analysis request failed. Please try again.',
          });
        });
      } else {
        console.error('❌ AI Gateway not connected');
        setIsThinking(false);
        settleAssistantPlaceholder({
          content: 'Sorry, the AI assistant is not connected. Please try again.',
        });
      }

      // Clear the pending request
      triggerAnalysis(null);
    }
  }, [pendingAnalysisRequest, triggerAnalysis]);

  /**
   * Dispatch a slash command. Working commands fire immediately;
   * coming-soon ones surface a friendly inline message so the
   * namespace exists in the user's mental model before the
   * implementation lands.
   */
  /** Resolve the active patient id from the chip rail with page-
   *  context fallback. Used by every command that needs a patient. */
  const resolvePatientId = (): string | undefined => {
    const patientChip = contextChips.find((c) => c.type === 'patient');
    return (
      patientChip?.id ||
      currentPatientContext?.id ||
      pageContext.currentPatient?.id
    );
  };

  const handleSlashCommand = (
    command: SlashCommandDef,
    args: string,
  ): boolean => {
    if (command.name === 'help') {
      const fmt = (c: SlashCommandDef) =>
        `• \`/${c.name}${c.argHint ? ' ' + c.argHint : ''}\` — ${c.description}`;
      const helpText =
        '**Available commands**\n' + SLASH_COMMANDS.map(fmt).join('\n');
      setChatMessages((prev) => [
        ...prev,
        { role: 'user', content: '/help' },
        { role: 'assistant', content: helpText },
      ]);
      setMessage('');
      return true;
    }

    if (command.name === 'clear' || command.name === 'new') {
      setActiveChatId(null);
      // Force the next send onto a fresh backend thread (both /new and
      // /clear start a clean conversation from the user's perspective).
      forceNewThreadRef.current = true;
      setChatMessages([]);
      streamingContentRef.current = '';
      setMessage('');
      toast.success(
        command.name === 'new' ? 'Started a new chat' : 'Conversation cleared',
      );
      return true;
    }

    if (command.name === 'book') {
      const patientId = resolvePatientId();
      if (!patientId) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'user', content: '/book' },
          {
            role: 'assistant',
            content:
              'Add a patient to the context (open a patient and click "Add to AI Context") and try again.',
          },
        ]);
        setMessage('');
        return true;
      }
      // Hand off to the existing booking page with the patient
      // pre-filled. Closing the bubble keeps the booking flow
      // unobstructed; reopening the bubble afterward returns the
      // user to the same chat thread (chatId is server-allocated).
      setMessage('');
      setShowAssistant(false);
      navigateTo('add-appointment', { patientId });
      return true;
    }

    const analysisType = ANALYSIS_BY_COMMAND[command.name];
    if (analysisType) {
      const patientId = resolvePatientId();
      if (!patientId) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'user', content: `/${command.name}` },
          {
            role: 'assistant',
            content:
              'Add a patient to the context (open a patient and click "Add to AI Context") and try again.',
          },
        ]);
        setMessage('');
        return true;
      }
      // Reuse the existing analysis effect — it already handles the
      // socket call, persistence to the active thread, and rendering.
      // `focus` propagates the user's `/cmd <args>` text down to the
      // AnalysisProcessor where it's appended to the prompt as
      // `USER FOCUS:` so the LLM narrows scope.
      const focus = args.trim();
      triggerAnalysis({
        analysisType,
        context: {
          patientId,
          includeHistory: true,
          includeMedications:
            analysisType === 'medication-safety-check' ||
            analysisType === 'patient-clinical-assessment',
          includeLabOrders:
            analysisType === 'patient-clinical-assessment' ||
            analysisType === 'patient-risk-assessment' ||
            analysisType === 'lab-results-interpretation',
          includeAppointments: analysisType === 'patient-clinical-assessment',
        },
        ...(focus ? { focus } : {}),
      });
      setMessage('');
      return true;
    }

    return false;
  };

  // Auto-send queued by another surface ("Chat about this" on the scan
  // inbox). Starts a FRESH thread — appending a scanned document to
  // whatever thread the bubble last held would be confusing. The
  // consumed-ref guard keeps StrictMode's double effect invoke (and any
  // re-render while the store value is being cleared) from double-sending.
  const consumedChatSendRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingChatMessage) return;
    if (consumedChatSendRef.current === pendingChatMessage) return;
    consumedChatSendRef.current = pendingChatMessage;
    setPendingChatMessage(null);
    // Same reset as /new — including the SYNCHRONOUS ref clear: the
    // send below runs before the state→ref sync effect, and a stale
    // ref would pin the turn to the old thread (chatId beats newThread
    // server-side).
    setActiveChatId(null);
    activeChatIdRef.current = null;
    forceNewThreadRef.current = true;
    setChatMessages([]);
    streamingContentRef.current = '';
    void handleSendMessage(pendingChatMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChatMessage]);

  const handleSendMessage = async (messageOverride?: string) => {
    // TTS audio arrives async — unlock the shared element now, while
    // we're still inside the send click/keypress (iOS refuses
    // un-gestured playback otherwise).
    if (voiceSettings.enableTTS) primeTtsAudio();
    const textToSend = (typeof messageOverride === 'string' ? messageOverride : message).trim();
    if (!textToSend) return;
    // AI not entitled (post-trial / out of quota / stack down): the composer
    // shows a subscribe/degrade panel; hard-block the send here too.
    if (!assistantEnabledRef.current) return;
    // A new turn supersedes the previous truncation.
    setLastTruncated(false);

    // Slash command? Route through the dedicated dispatcher.
    const matched = matchSlashCommand(SLASH_COMMANDS, textToSend);
    if (matched) {
      if (handleSlashCommand(matched.command, matched.args)) return;
    }

    // Block sending while a previous request is still in flight. Without
    // this gate, a double-send (Enter + click, or two Enters) would
    // interrupt the running stream and start a new one — the indicator
    // disappears mid-flight and the user gets no feedback that their
    // second send was accepted at all. Surface a quick shake on the
    // input instead so the action is acknowledged but ignored.
    if (isThinking || isStreaming) {
      const el = messageInputRef.current;
      if (el) {
        el.classList.remove('maie-shake');
        // Force a reflow so re-adding the class re-triggers the animation.
        void el.offsetWidth;
        el.classList.add('maie-shake');
      }
      return;
    }

    // Interrupt any currently playing TTS audio
    ttsHandleRef.current?.stop();
    ttsHandleRef.current = null;
    if (ttsPauseTimerRef.current) {
      clearTimeout(ttsPauseTimerRef.current);
      ttsPauseTimerRef.current = null;
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingAudioRef.current = false;
    // Stamp a fresh owner for this turn's TTS so chunks that arrive
    // after this point are attributed to the new send, and any chunks
    // still arriving from the previous send are recognized as stale
    // (their tag won't match, so playNextAudio drops them).
    audioOwnerTurnIdRef.current = `${activeChatIdRef.current ?? 'pending'}#${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    const userMessage = textToSend;
    setMessage('');
    setPendingPrompt('');
    setShowContextPreview(false);

    // Snapshot the patient bound to this turn. The chip rail is the
    // canonical source of patient binding — the auto-attach effect
    // surfaces page-context patients as a chip the user can dim (set
    // enabled=false) to opt out of patient context for unrelated
    // questions. A missing or DISABLED chip means "don't send the
    // patient" for this turn.
    //
    // The `currentPatientContext` fallback is kept only for the
    // voice-flow race window where the chip-attach useEffect hasn't
    // fired yet but the patient is already known programmatically;
    // pageContext.currentPatient is intentionally NOT a fallback (it
    // would re-introduce the silent auto-include the chip mechanism
    // is meant to replace).
    const resolvedPatientChip = contextChips.find(
      (c) => c.type === 'patient' && c.enabled !== false,
    );
    const turnPatient =
      resolvedPatientChip
        ? { id: resolvedPatientChip.id, label: resolvedPatientChip.label }
        : currentPatientContext
        ? { id: currentPatientContext.id, label: currentPatientContext.name }
        : null;

    // Add user message to chat
    // Push the user message AND an empty assistant placeholder atomically.
    // The placeholder renders typing dots (see chatMessages.map) and stays
    // until the first token replaces its content. Pushing it up-front
    // closes the dead-air window between "thinking" and "first token".
    setChatMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: userMessage,
        patientContext: turnPatient
          ? { id: turnPatient.id, name: turnPatient.label }
          : undefined,
      },
      { role: 'assistant', content: '' },
    ]);
    setIsThinking(true);

    // Previously we detached the chip here on the theory that the
    // thread was now patient-bound server-side and the chip had done
    // its job. Removed 2026-05-13: with the chip as a persistent,
    // toggle-able page indicator (Copilot-sidebar style), it should
    // stay visible across turns so the user can dim/restore it
    // between questions without re-navigating. The backend still
    // routes by activeChatId for subsequent turns, but having the
    // chip visible also makes the implicit binding legible to the
    // user. (No-op kept here as a marker for the next refactor.)

    try {
      if (!aiGatewayService.isConnected()) {
        throw new Error('AI Gateway not connected');
      }

      // Patient ID for this turn comes from the same `turnPatient`
      // resolution as the UI patient pill, so the indicator and the
      // backend payload can never disagree. With pageContext.currentPatient
      // no longer in the fallback chain (the chip auto-attach effect
      // handles patient-page binding now), a missing patient id here
      // means the user explicitly opted out of patient context for
      // this turn.
      const patientId = turnPatient?.id;

      const historyWithoutActions = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Idempotency key for this send-click. The backend dedupes
      // appendUserMessage by (thread_id, clientMessageId) so retries
      // from network glitches or auto-retry logic don't duplicate
      // the user row. Generated fresh per click — a different click
      // with the same text counts as a new send.
      const clientMessageId = (
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `cm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      );

      // Build the rich patient block from the same source as
      // `turnPatient` so the backend's CommandProcessor /
      // SemanticContextService and the UI patient-pill always agree.
      // Previously this used `currentPatientContext || pageContext.currentPatient`
      // which IGNORED the chip — so clicking "Add patient to context"
      // off the patient page set `patientId` but left
      // `context.currentPatient` empty, and the LLM had to bluff a
      // response without the patient profile block.
      const turnPatientForContext = turnPatient
        ? {
            id: turnPatient.id,
            name: turnPatient.label,
            // Preserve any richer fields the bubble has cached (age,
            // gender, etc.) when the chip happens to point at the same
            // patient as currentPatientContext.
            ...(currentPatientContext && currentPatientContext.id === turnPatient.id
              ? currentPatientContext
              : {}),
          }
        : null;

      const chatPayload = {
        message: userMessage,
        chatId: activeChatId ?? undefined,
        // Force a new server-side thread when the user ran /new or /clear, OR
        // when the bubble has no bound thread yet (a fresh open). Without the
        // latter, the server auto-resumed the user's LATEST global thread — so
        // the bubble dumped its messages into the last chat created elsewhere
        // (e.g. the /assistant workspace) instead of starting its own. The
        // server stamps a real chatId back via chat-start, pinned for the rest
        // of the session, so only this first turn force-creates.
        newThread: forceNewThreadRef.current || !activeChatId || undefined,
        patientId,
        clientMessageId,
        sessionId, // legacy, ignored server-side now
        context: {
          ...pageContext,
          currentPatient: turnPatientForContext,
        },
        conversationHistory: historyWithoutActions, // legacy
        enableTTS: voiceSettings.enableTTS && voiceSettings.ttsOnlyFloatingButton,
        ttsVoice: voiceSettings.ttsVoice || 'p230',
        ttsSpeed: voiceSettings.ttsSpeed || 0.9,
      };
      // One-shot: the flag only governs this first post-/new send. The
      // server returns a real chatId via chat-start, which we pin for
      // subsequent turns so the thread continues normally.
      forceNewThreadRef.current = false;

      // Reset streaming state. The empty assistant placeholder was
      // already pushed up-front (alongside the user message) so the
      // typing dots render immediately on send instead of after the
      // WebSocket round-trip.
      streamingContentRef.current = '';
      setIsStreaming(true);

      // Tag THIS stream with the chat thread it belongs to so deltas
      // arriving after a thread switch don't corrupt the visible
      // chat on the new thread. We resolve the owning chat id in
      // priority order:
      //   1. activeChatIdRef.current — the bubble's pinned thread.
      //   2. Server-assigned chatId from `chat-start` event below
      //      (auto-resume case where the client didn't pin one yet).
      //   3. `null` — the very first turn before either is known;
      //      handled specially in the delta filter below.
      const ownerChatIdAtSend = activeChatIdRef.current;
      let resolvedOwnerChatId: string | null = ownerChatIdAtSend;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onEvent = (event: string, data: any) => {
        switch (event) {
          case 'chat-start':
            // Context metadata received — streaming is about to begin.
            // Server stamps the resolved chat thread id here (auto-
            // resume case where the client didn't supply one); pin
            // the bubble to it for subsequent turns.
            setIsThinking(false);
            if (data?.chatId) {
              // Lock the owner id for downstream delta filtering even
              // if the bubble's activeChatId hasn't caught up yet
              // (state update is async).
              resolvedOwnerChatId = data.chatId;
              if (data.chatId !== activeChatId) {
                setActiveChatId(data.chatId);
              }
            }
            break;

          case 'llm-delta': {
            // Drop deltas that belong to a thread the user has moved
            // away from. Two cases:
            //   - resolvedOwnerChatId is set AND doesn't match the
            //     bubble's current activeChatIdRef → stale stream.
            //   - resolvedOwnerChatId is null (very first turn) AND
            //     the user has since switched to a known thread →
            //     also stale.
            // The backend persists the assistant message to the
            // original thread regardless, so the user gets the full
            // answer if they navigate back to it.
            const currentActive = activeChatIdRef.current;
            if (resolvedOwnerChatId !== null) {
              if (currentActive !== null && currentActive !== resolvedOwnerChatId) {
                break;
              }
            } else if (currentActive !== null) {
              break;
            }

            // Append token to the last (streaming) assistant message
            streamingContentRef.current += data.content || '';
            // Strip both tool-call and propose markers so users don't
            // see raw `[TOOL_CALL: …]` / `[PROPOSE: …]` JSON envelopes
            // in the bubble. The backend re-strips before persistence;
            // the per-item ProposalCard renders below the message.
            //
            // Propose markers carry nested `{items:[…]}` JSON so a
            // non-greedy regex misses the matching close. We instead
            // walk from the `[PROPOSE:` head, count braces to find the
            // end, and slice it out. While a marker is mid-stream
            // (no matching `)]` yet) we hide everything from the head
            // onward — otherwise the user briefly sees raw JSON.
            const displayContent = stripStreamingMarkers(
              streamingContentRef.current,
            ).trim();
            setChatMessages(prev => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last && last.role === 'assistant') {
                updated[updated.length - 1] = { ...last, content: displayContent };
              }
              return updated;
            });
            break;
          }

          case 'llm-complete':
            // A stream chunk finished. During tool calls there may be multiple
            // llm-complete events (initial stream + continuation after tool result).
            // Don't overwrite content from full_response — streamingContentRef
            // is the source of truth since it accumulates across all streams.
            // Don't set isStreaming=false here — wait for the Promise resolution
            // (assistant.chat-complete) to avoid premature finalization.
            setIsThinking(false);
            // `length` means the model hit its token cap and the answer
            // stops mid-sentence. Without surfacing it the doctor just
            // sees the reply end abruptly and can't tell a cut-off from a
            // finished thought. A tool round emits several llm-complete
            // events, so the LAST one wins — an early chunk capping out
            // doesn't mean the final answer did.
            setLastTruncated(
              (data as { finish_reason?: string })?.finish_reason === 'length',
            );
            break;
        }
      };

      aiGatewayService.assistantChat(chatPayload, onEvent).then((data) => {
        setIsStreaming(false);
        setIsThinking(false);

        // Drop the entire post-stream update if the user has moved to
        // a different chat thread while we were waiting on the reply.
        // The backend persisted the assistant message to the original
        // thread already; updating chatMessages here would write the
        // old thread's data into the new thread's view. Going back to
        // the original thread via the chat list re-fetches it fresh.
        const currentActive = activeChatIdRef.current;
        if (resolvedOwnerChatId !== null) {
          if (currentActive !== null && currentActive !== resolvedOwnerChatId) {
            return;
          }
        } else if (currentActive !== null) {
          return;
        }

        // Handle clear chat command
        if (data?.clearChat) {
          setChatMessages([{
            role: 'assistant',
            content: data.message || 'Conversation cleared.',
          }]);
          return;
        }

        // Handle interruption — keep partial content as-is
        if (data?.interrupted) {
          return;
        }

        // Detect a degenerate / empty response and replace the assistant
        // bubble with a "no response" hint. Three failure modes the LLM
        // exhibits in prod that we never want the user to see verbatim:
        //   - Zero tokens: model emits EOS immediately, bubble stays blank.
        //   - Turn-marker loop: Gemma-4 sometimes spirals on the literal
        //     `<end_of_turn>` / `<start_of_turn>` strings when the prompt
        //     confuses it (562-token junk burst observed 2026-05-12).
        //   - Single-word loop: low temperature + the empty `<|channel>
        //     thought\n<channel|>` prefill recency-locks the model on
        //     one word and it repeats until the server-side abort fires
        //     (a partial `thought thought thought thought thought…` burst
        //     observed 2026-05-19). The server cuts it short, but the
        //     surviving text is still meaningless.
        // Cheap heuristic: if the visible content is empty, >70% of it
        // looks like turn markers, OR a single word makes up >60% of
        // the whole-word tokens (and we have at least 4 words), treat
        // it as failed.
        const finalText = streamingContentRef.current.trim();
        const isTurnMarkerJunk =
          finalText.length > 0 &&
          (finalText.replace(/<[/_-]?(?:end|start)[_-]of[_-]turn>?/gi, '').trim()
            .length /
            finalText.length) <
            0.3;
        const wordTokens = finalText
          .toLowerCase()
          .match(/[a-z][a-z'-]*/g) ?? [];
        let isWordLoop = false;
        if (wordTokens.length >= 4) {
          const counts = new Map<string, number>();
          for (const w of wordTokens) counts.set(w, (counts.get(w) ?? 0) + 1);
          const topCount = Math.max(...counts.values());
          isWordLoop = topCount / wordTokens.length > 0.6;
        }
        if (!finalText || isTurnMarkerJunk || isWordLoop) {
          setChatMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            const placeholder =
              'No response was generated. Please rephrase your question or try again.';
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, content: placeholder };
            }
            return next;
          });
          return;
        }

        // Surface any [PROPOSE: …] markers the model emitted as a
        // ProposalCard with per-item Add / Skip buttons. The backend
        // ships `actions[]` with `{type, parameters: {patientId, items[]}}`;
        // we layer patientName from the turn context here so the card
        // header reads "Lab orders suggested for <name>" without an
        // extra DB round-trip on the gateway.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const proposalActionsRaw = (data?.actions as Array<any>) || [];
        if (proposalActionsRaw.length > 0) {
          const proposalActions = proposalActionsRaw
            .filter(
              (a) =>
                a?.type === 'propose_lab_orders' ||
                a?.type === 'propose_imaging_orders' ||
                a?.type === 'propose_prescriptions' ||
                a?.type === 'propose_patient',
            )
            .map((a) => ({
              ...a,
              parameters: {
                ...(a.parameters || {}),
                patientId:
                  a.parameters?.patientId ?? turnPatient?.id ?? patientId,
                patientName:
                  a.parameters?.patientName ?? turnPatient?.label,
              },
            }));
          if (proposalActions.length > 0) {
            setPendingActions(proposalActions);
          }
        }

        // Capture the encounter citation map from the chat-complete
        // payload onto the assistant message so the markdown renderer
        // can resolve `[E<n>]` markers to clickable encounter links.
        // `[S]` and `[T:tool]` don't need a map — they resolve from
        // constants client-side.
        const rawMap =
          (data?.encounterIdMap as
            | Array<{ marker: string; encounterId: string; visitDate?: string }>
            | undefined) ??
          (data?.contextMeta?.encounterIdMap as
            | Array<{ marker: string; encounterId: string; visitDate?: string }>
            | undefined);
        if (rawMap && rawMap.length > 0) {
          setChatMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, encounterIdMap: rawMap };
            }
            return next;
          });
        }

        // Restart recording in continuous voice mode
        const currentVoiceSettings = useVoiceStore.getState().settings;
        const isTTSActive = currentVoiceSettings.enableTTS && currentVoiceSettings.ttsOnlyFloatingButton !== false;
        if (isContinuousModeRef.current && !isTTSActive) {
          setTimeout(() => {
            if (!isRecordingRef.current && isContinuousModeRef.current) {
              startVoiceRecording();
            }
          }, 500);
        }
      }).catch((err) => {
        console.error('Chat request failed:', err);
        setIsStreaming(false);
        setIsThinking(false);
        // Replace the empty assistant placeholder with the error text
        // so the dots-bubble doesn't strand next to a real error bubble.
        setChatMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && last.content === '') {
            next[next.length - 1] = {
              ...last,
              content: 'Sorry, I encountered an error. Please try again.',
            };
            return next;
          }
          return [
            ...next,
            { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
          ];
        });
      });
    } catch (error) {
      console.error('Error sending chat message:', error);
      setIsStreaming(false);
      // Same placeholder-replacement logic as the .catch above.
      setChatMessages(prev => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && last.content === '') {
          next[next.length - 1] = {
            ...last,
            content: 'Sorry, I encountered an error. Please try again.',
          };
          return next;
        }
        return [
          ...next,
          { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
        ];
      });
      setIsThinking(false);
    }
  };

  const handleConfirmActions = async () => {
    if (pendingActions.length === 0) return;

    setIsThinking(true);
    try {
      await executeActions(pendingActions);
      setPendingActions([]);
    } catch (error) {
      console.error('Error executing actions:', error);
    } finally {
      setIsThinking(false);
    }
  };

  const handleCancelActions = () => {
    setPendingActions([]);
  };

  // Function to stop all TTS audio (used for interruption)
  const stopAllAudio = () => {
    console.log('🛑 Stopping all TTS audio');

    ttsHandleRef.current?.stop();
    ttsHandleRef.current = null;
    if (ttsPauseTimerRef.current) {
      clearTimeout(ttsPauseTimerRef.current);
      ttsPauseTimerRef.current = null;
    }
    // Stop current audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingAudioRef.current = false;

    // Send stop signal to backend
    if (aiGatewayService.isConnected()) {
      aiGatewayService.assistantStopTts();
    }
  };

  // Handle manual click selection - sends selection number to backend LLM flow
   
  const handleManualSelection = async (optionId: string) => {
    if (!activeSelectionContext) return;

     
    const selectionCtx = activeSelectionContext as { options: Array<{ id: string; label: string; description?: string }>; message?: string };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectedOption = selectionCtx.options.find((opt: any) => opt.id === optionId);
    if (!selectedOption) return;

    console.log('🖱️ Manual click selection:', selectedOption);

    // Instead of handling locally, send the selection number to the backend
    // This ensures the backend knows about the selection and confirmation state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const optionIndex = selectionCtx.options.findIndex((opt: any) => opt.id === optionId);
    const selectionMessage = `${optionIndex + 1}`;

    console.log(`📤 Sending click selection as message: "${selectionMessage}" (will go through backend LLM flow)`);
    await handleSendMessage(selectionMessage);
  };

  const handleSelectPatient = (patientId: string, patientName: string, actionType?: string) => {
    // Add selection to chat
    setChatMessages(prev => [
      ...prev,
      { role: 'user', content: `Selected: ${patientName}` }
    ]);

    // Update current patient context
    setCurrentPatientContext({ id: patientId, name: patientName });

    // Find the original action from pending actions
    const originalAction = pendingActions.find(a => a.type === actionType);

    // Clear pending actions
    setPendingActions([]);

    // Check if the action has a follow-up action from backend (intelligent chaining)
    let followUpAction: AIAction;

    if (originalAction?.followUpAction) {
      // Backend specified the follow-up action - replace placeholders with actual values
      followUpAction = {
        ...originalAction.followUpAction,
        parameters: {
          ...originalAction.followUpAction.parameters,
          patientId: originalAction.followUpAction.parameters.patientId === '{{selectedPatientId}}'
            ? patientId
            : originalAction.followUpAction.parameters.patientId,
          patientName: originalAction.followUpAction.parameters.patientName === '{{selectedPatientName}}'
            ? patientName
            : originalAction.followUpAction.parameters.patientName,
        }
      };
    } else {
      // Fallback: Default to opening patient details (no specific resource requested)
      followUpAction = {
        type: 'open_patient',
        parameters: { patientId, patientName },
        description: `Open patient ${patientName}`
      } as AIAction;
    }

    executeActions([followUpAction]);
  };

  const handleSelectLabOrder = (labOrderId: string, patientId: string, patientName: string) => {
    // Find the selected lab order details
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectedOrder = (pendingActions[0]?.parameters?.labOrders as any[] | undefined)?.find((o: any) => o.id === labOrderId);
    const orderDetails = selectedOrder
      ? `${formatDate(selectedOrder.orderDate)} - ${selectedOrder.status} (${selectedOrder.priority} priority, ${selectedOrder.tests?.length || 0} test(s))`
      : 'lab order';

    // Add selection to chat with details
    setChatMessages(prev => [
      ...prev,
      { role: 'user', content: `Selected: Lab Order for ${patientName} - ${orderDetails}` }
    ]);

    // Update current patient context
    setCurrentPatientContext({ id: patientId, name: patientName });

    setPendingActions([]);
    executeActions([{
      type: 'open_lab_order',
      parameters: {
        labOrderId,
        patientId,
        patientName
      },
      description: `Open lab order`
    }]);
  };

  const handleSelectAppointment = (appointmentId: string, patientId: string, patientName: string) => {
    // Find the selected appointment details
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectedAppt = (pendingActions[0]?.parameters?.appointments as any[] | undefined)?.find((a: any) => a.id === appointmentId);
    const apptDetails = selectedAppt
      ? `${selectedAppt.appointmentDate ? new Date(selectedAppt.appointmentDate).toLocaleDateString() : 'Date not set'} at ${selectedAppt.appointmentTime || 'Time not set'} - ${selectedAppt.appointmentType} (${selectedAppt.status})`
      : 'appointment';

    // Add selection to chat with details
    setChatMessages(prev => [
      ...prev,
      { role: 'user', content: `Selected: Appointment for ${patientName} - ${apptDetails}` }
    ]);

    // Update current patient context
    setCurrentPatientContext({ id: patientId, name: patientName });

    setPendingActions([]);
    executeActions([{
      type: 'open_appointment',
      parameters: {
        appointmentId,
        patientId,
        patientName
      },
      description: `Open appointment`
    }]);
  };

  const handleSelectMedicalRecord = (medicalRecordId: string, patientId: string, patientName: string) => {
    // Find the selected medical record details
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectedRecord = (pendingActions[0]?.parameters?.medicalRecords as any[] | undefined)?.find((r: any) => r.id === medicalRecordId);
    const recordDetails = selectedRecord
      ? `${formatDate(selectedRecord.visitDate)} - ${selectedRecord.visitType} (${selectedRecord.status})${selectedRecord.chiefComplaint ? ` - ${selectedRecord.chiefComplaint}` : ''}`
      : 'medical record';

    // Add selection to chat with details
    setChatMessages(prev => [
      ...prev,
      { role: 'user', content: `Selected: Medical Record for ${patientName} - ${recordDetails}` }
    ]);

    // Update current patient context
    setCurrentPatientContext({ id: patientId, name: patientName });

    setPendingActions([]);
    executeActions([{
      type: 'open_medical_record',
      parameters: {
        medicalRecordId,
        patientId,
        patientName
      },
      description: `Open medical record`
    }]);
  };

  // Tear down the VAD analyser/interval. Safe to call from anywhere — the
  // VAD tick self-clears on isRecordingRef false, but doing it eagerly keeps
  // the dying AnalyserNode from doing one more pointless 50ms read.
  const stopVadAnalysis = () => {
    if (vadCheckIntervalRef.current) {
      clearInterval(vadCheckIntervalRef.current);
      vadCheckIntervalRef.current = null;
    }
    if (vadAudioContextRef.current) {
      try { void vadAudioContextRef.current.close(); } catch { /* ignore */ }
      vadAudioContextRef.current = null;
    }
    vadAnalyserRef.current = null;
  };

  // Stop and forget the captured mic stream. Used wherever onstop can't be
  // relied on to release the OS mic (recorder already inactive, error path,
  // session-mismatch, etc.) so the browser indicator drops promptly.
  const releaseMicStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
      mediaStreamRef.current = null;
    }
    stopVadAnalysis();
  };

  // Setup AI Gateway connection and event subscriptions

  useEffect(() => {
    console.log('🔌 Connecting to AI Gateway (singleton)...');

    // Connect via singleton service (no-op if already connected)
    aiGatewayService.connect().then(() => {
      console.log('✅ AI Gateway connected!');
      gatewayConnectedRef.current = true;
    }).catch((err) => {
      console.error('❌ AI Gateway connection failed:', err);
      gatewayConnectedRef.current = false;
    });

    // Subscribe to transcription events
    const unsubTranscription = aiGatewayService.on('assistant.transcription', (data: { text: string; sessionId?: string }) => {
      // Same shared-socket guard as transcription-complete below: ignore
      // partials from a session this bubble didn't start (e.g. advanced
      // voice mode's), so we don't stuff its transcript into our input.
      if (
        data.sessionId
          ? data.sessionId !== voiceSessionIdRef.current
          : !isRecordingRef.current
      ) {
        return;
      }
      console.log('🎤 Voice transcription received:', data.text);
      setVoiceTranscript(prev => prev + ' ' + data.text);
      setMessage(prev => prev + ' ' + data.text);
    });

    // Subscribe to transcription-complete events
    const unsubTranscriptionComplete = aiGatewayService.on('assistant.transcription-complete', (data: { text: string; sessionId?: string }) => {
      // This socket is a SINGLETON shared with advanced voice mode's capture
      // hook. Only handle transcripts from a recording THIS bubble started —
      // otherwise the always-mounted bubble hears VoiceMode's transcript and
      // fires its own duplicate chat + TTS. When the event carries a
      // sessionId (current backend) match it against ours; without one (older
      // backend) fall back to "were we actually recording?".
      if (
        data.sessionId
          ? data.sessionId !== voiceSessionIdRef.current
          : !isRecordingRef.current
      ) {
        return;
      }
      console.log('✅ Transcription complete:', data.text);

      // Filter out wake word from transcription
      let transcribedText = data.text || '';
      const wakeWordPatterns = [
        /^hey\s+ma+i+e*\s*/i,  // "hey maie", "hey maaie", etc.
        /^hey\s+may\s*/i,       // "hey may"
        /^hey\s+my\s*/i,        // "hey my"
      ];

      for (const pattern of wakeWordPatterns) {
        transcribedText = transcribedText.replace(pattern, '').trim();
      }

      console.log('📝 Filtered transcription:', transcribedText);
      setMessage(transcribedText); // Set final complete transcription

      // Clear recording and transcribing states
      setIsRecording(false);
      setIsListening(false);
      setIsTranscribing(false);

      // Auto-send if enabled and has content after filtering
      if (voiceSettings.autoSendAfterTranscription && transcribedText?.trim()) {
        console.log('📤 Auto-sending backend transcription...');
        // Defer one tick so any state listeners that react to the
        // transcription (e.g. auto-attaching a patient chip from a
        // recently-mentioned name, or hydrating pageContext after the
        // bubble first opens) have time to land BEFORE handleSendMessage
        // snapshots `contextChips` / `currentPatientContext`. 200ms is
        // imperceptible to the user but covers the React commit + the
        // chip-rail effect cycle.
        setTimeout(() => handleSendMessage(transcribedText), 200);
      }
    });

    // Subscribe to connection-ready
    const unsubConnectionReady = aiGatewayService.on('assistant.connection-ready', () => {
      console.log('✅ HEIDI.LLM connection ready for transcription');
    });

    // Subscribe to errors
    const unsubError = aiGatewayService.on('error', (data: unknown) => {
      const code = (data as { code?: string } | null)?.code;
      const message = (data as { message?: string } | null)?.message;
      // AI being down is an expected, recoverable state — not an error to
      // alarm the clinician with. Log it quietly and show a calm notice;
      // the rest of the EMR keeps working in manual mode.
      const unavailable = code === 'AI_UNAVAILABLE';
      if (unavailable) {
        console.warn('AI gateway unavailable:', message);
      } else {
        console.error('❌ AI gateway error:', data);
      }
      // Capture whether STT was actually in flight BEFORE we clear the
      // flag below — otherwise we can't tell a transcription error apart
      // from any other socket-level error (analysis, lab check, etc.)
      // and would mislabel a "Lab analysis failed" as "Couldn't transcribe".
      const wasTranscribing = isTranscribing || isRecordingRef.current;
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsListening(false);
      setIsThinking(false);
      setIsTranscribing(false);
      releaseMicStream();
      if (unavailable) {
        toast.error(
          message || 'AI is temporarily unavailable — please try again shortly.',
        );
      } else if (wasTranscribing || /transcription|whisper|stt/i.test(message ?? '')) {
        toast.error("Couldn't transcribe that — please try again.");
      } else if (message) {
        toast.error(message);
      } else {
        toast.error('AI request failed — please try again.');
      }
    });

    // Function to play audio from queue
    const playNextAudio = () => {
      if (isPlayingAudioRef.current || audioQueueRef.current.length === 0) {
        // No more audio to play - end continuous mode and wait for next wake word
        if (!isPlayingAudioRef.current && audioQueueRef.current.length === 0 && shouldRestartRecordingRef.current) {
          console.log('✅ TTS playback complete - ending conversation, waiting for wake word');
          shouldRestartRecordingRef.current = false;

          // Exit continuous mode
          isContinuousModeRef.current = false;

          // Stop any active recording
          if (isRecordingRef.current) {
            console.log('⏹️ Stopping recording after TTS complete');
            stopVoiceRecording();
          }

          // Session is now over - user must say "Maie" again to start new conversation
          console.log('👂 Ready for wake word detection...');
        }
        return;
      }

      // Drop any queued chunks that don't belong to the current turn.
      // The ownerTurnId is captured at receive time; on thread switch
      // OR on a new send we bump audioOwnerTurnIdRef, which makes every
      // chunk from the previous turn stale and gets dropped here.
      while (audioQueueRef.current.length > 0) {
        const head = audioQueueRef.current[0];
        if (head.ownerTurnId === audioOwnerTurnIdRef.current && head.ownerTurnId !== '') break;
        console.log(
          `🔇 Dropping stale TTS chunk (owner=${head.ownerTurnId}, expected=${audioOwnerTurnIdRef.current}): "${head.text}"`,
        );
        audioQueueRef.current.shift();
      }
      if (audioQueueRef.current.length === 0) {
        return;
      }

      isPlayingAudioRef.current = true;
      const { audio: audioData, text } = audioQueueRef.current.shift()!;

      console.log(`🎵 Playing audio: "${text}"`);

      // Element-first playback with Web Audio fallback; onDone fires
      // exactly once so the queue always advances (pre-fix a rejected
      // play() stalled it forever with isPlayingAudioRef stuck true).
      ttsHandleRef.current = playWavBytes(new Uint8Array(audioData), () => {
        isPlayingAudioRef.current = false;
        ttsHandleRef.current = null;
        // Natural beat between sentences so VITS doesn't rush; skip the
        // pause when nothing's queued so playback ends promptly.
        if (audioQueueRef.current.length > 0) {
          ttsPauseTimerRef.current = setTimeout(() => {
            ttsPauseTimerRef.current = null;
            playNextAudio();
          }, interSentencePauseMs(text));
        } else {
          playNextAudio();
        }
      });
    };

    // TTS Audio Chunk listener
    const unsubTtsChunk = aiGatewayService.on('assistant.tts-audio-chunk', (data: { index: number; total: number; text: string; audio: ArrayBuffer }) => {
      console.log(`🔊 Received TTS chunk ${data.index + 1}/${data.total}: "${data.text}"`);

      // Tag with the turn owner that's active right now so playNextAudio
      // can drop chunks that no longer belong (user switched threads or
      // double-sent while this one was still streaming). If the owner
      // is empty the user has no active turn — chunks are stragglers
      // from a turn we've already moved past; skip the push entirely.
      const currentTurnOwner = audioOwnerTurnIdRef.current;
      if (!currentTurnOwner) {
        console.log(`🔇 Dropping orphan TTS chunk (no active turn): "${data.text}"`);
        return;
      }
      audioQueueRef.current.push({
        audio: data.audio,
        text: data.text,
        ownerTurnId: currentTurnOwner,
      });

      // On first TTS chunk, restart recording for interruption detection
      if (data.index === 0 && isContinuousModeRef.current && !isRecordingRef.current) {
        console.log('🎤 TTS started - restarting recording for interruption detection');
        setTimeout(() => {
          if (!isRecordingRef.current) {
            startVoiceRecording();
          }
        }, 200); // Small delay to ensure TTS playback starts
      }

      // Start playing if not already playing
      playNextAudio();
    });

    // TTS Complete listener
    const unsubTtsComplete = aiGatewayService.on('assistant.tts-complete', () => {
      console.log('✅ TTS streaming complete');
      // Mark that we should restart recording after audio finishes playing
      if (isContinuousModeRef.current) {
        shouldRestartRecordingRef.current = true;
      }
    });

    return () => {
      console.log('🔌 Cleaning up AI Gateway event subscriptions...');
      unsubTranscription();
      unsubTranscriptionComplete();
      unsubConnectionReady();
      unsubError();
      unsubTtsChunk();
      unsubTtsComplete();
      gatewayConnectedRef.current = false;
    };
  }, []);

  const handleVoiceInput = async () => {
    console.log('🎙️ handleVoiceInput called, isRecording:', isRecording);
    if (isRecording) {
      // Stop recording AND exit continuous mode
      console.log('⏹️ Stopping voice recording and exiting continuous mode...');
      isContinuousModeRef.current = false;
      shouldRestartRecordingRef.current = false;
      stopAllAudio(); // Stop any playing TTS
      stopVoiceRecording();
    } else {
      // Start recording — and unlock the TTS element inside this tap
      // so the spoken reply is allowed to play when it arrives.
      if (voiceSettings.enableTTS) primeTtsAudio();
      console.log('▶️ Starting voice recording...');
      startVoiceRecording();
    }
  };

  const startVoiceRecording = async () => {
    try {
      console.log('🎬 startVoiceRecording called');

      // Hard gate: if AI isn't usable (off-plan / out of quota / stack
      // down) never open the mic or start a backend session. Covers the
      // push-to-talk / manual-activation entry points that bypass the
      // wake-word `enabled` flag. Surfaces the reason so it's not silent.
      if (!assistantEnabledRef.current) {
        toast.info(
          assistantAffordance.message ??
            t('aiAssistant.voiceUnavailableAiOff'),
        );
        return;
      }

      // CRITICAL: Stop any existing MediaRecorder first to prevent chunk overlap
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('⚠️ Stopping existing MediaRecorder before starting new one');
        try {
          mediaRecorderRef.current.stop();
          // Clear the ref immediately to prevent ondataavailable from firing
          mediaRecorderRef.current = null;
        } catch (e) {
          console.warn('Failed to stop existing MediaRecorder:', e);
        }
      }
      // Release any prior mic stream + VAD wiring synchronously. Without this,
      // the previous session's MediaStream tracks leak (its onstop early-returns
      // on session-id mismatch) and its AnalyserNode keeps consuming audio.
      releaseMicStream();

      setIsRecording(true);
      isRecordingRef.current = true;
      setIsListening(true);
      setVoiceTranscript('');

      // Reset flags for new recording session
      isStoppingRef.current = false;
      finalChunkSentRef.current = false;
      speechFramesCountRef.current = 0; // Reset speech frame counter

      // Generate session ID
      voiceSessionIdRef.current = `ai-voice-${Date.now()}_${Math.random().toString(36).substring(7)}`;
      console.log('📝 Session ID:', voiceSessionIdRef.current);

      // Check if AI Gateway is connected
      if (!aiGatewayService.isConnected()) {
        console.error('❌ AI Gateway not connected!');
        throw new Error('AI Gateway not connected');
      }

      console.log('📤 Emitting start-session to backend...');
      // Start session with backend (transcription only, not SOAP generation)
      aiGatewayService.assistantStartSession({
        sessionId: voiceSessionIdRef.current,
        // Respect the doctor's transcription-language pref (en/ar/auto) — the
        // same one the encounter scribe uses — instead of forcing English.
        language: useVoiceStore.getState().settings.sttLanguage || 'en',
        transcriptionOnly: true, // Flag to indicate we only want transcription
      });

      // Get microphone access
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      console.log('✅ Microphone access granted');

      // Setup VAD (Voice Activity Detection) for auto-stop
      const audioContext = new AudioContext();
      vadAudioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      vadAnalyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Reset silence counter
      vadSilenceFramesRef.current = 0;

      // Start VAD monitoring with improved speech detection
      const VOLUME_THRESHOLD = 0.01; // Minimum volume to consider
      const SPEECH_FREQUENCY_MIN = 85; // Hz - Lower bound of human speech
      const SPEECH_FREQUENCY_MAX = 3400; // Hz - Upper bound of human speech
      const SPEECH_ENERGY_RATIO = 0.4; // % of energy that must be in speech frequencies
      const SILENCE_DURATION_FRAMES = 100; // ~5.0 seconds (100 frames * 50ms per check) - longer pause tolerance for natural speech
      const GRACE_PERIOD_FRAMES = 20; // ~1.0 second grace period before starting to count silence

      let frameCount = 0;
      let hasDetectedSpeech = false;
      let strongSpeechFrames = 0; // Track consecutive strong speech frames for interruption

      vadCheckIntervalRef.current = window.setInterval(() => {
        if (!isRecordingRef.current) {
          if (vadCheckIntervalRef.current) {
            clearInterval(vadCheckIntervalRef.current);
            vadCheckIntervalRef.current = null;
          }
          return;
        }

        frameCount++;

        // Get time domain data for volume
        const bufferLength = analyser.frequencyBinCount;
        const timeData = new Uint8Array(bufferLength);
        analyser.getByteTimeDomainData(timeData);

        // Calculate RMS (volume)
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const normalized = (timeData[i] - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / bufferLength);

        // Get frequency data for speech detection
        const freqData = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(freqData);

        // Analyze frequency distribution
        const sampleRate = audioContext.sampleRate;
        const binSize = sampleRate / (2 * bufferLength);
        const speechBinMin = Math.floor(SPEECH_FREQUENCY_MIN / binSize);
        const speechBinMax = Math.floor(SPEECH_FREQUENCY_MAX / binSize);

        let totalEnergy = 0;
        let speechEnergy = 0;

        for (let i = 0; i < bufferLength; i++) {
          const energy = freqData[i] * freqData[i];
          totalEnergy += energy;

          if (i >= speechBinMin && i <= speechBinMax) {
            speechEnergy += energy;
          }
        }

        const speechEnergyRatio = totalEnergy > 0 ? speechEnergy / totalEnergy : 0;
        const isSpeech = rms > VOLUME_THRESHOLD && speechEnergyRatio > SPEECH_ENERGY_RATIO;

        // High threshold for interruption (3x volume, 1.3x speech ratio)
        // Balanced to prevent false triggers while still being responsive
        const INTERRUPTION_VOLUME_THRESHOLD = VOLUME_THRESHOLD * 3; // 0.03 instead of 0.01
        const INTERRUPTION_SPEECH_RATIO = SPEECH_ENERGY_RATIO * 1.3; // 52% instead of 40%
        const isStrongSpeech = rms > INTERRUPTION_VOLUME_THRESHOLD && speechEnergyRatio > INTERRUPTION_SPEECH_RATIO;

        if (isSpeech) {
          // Human speech detected
          hasDetectedSpeech = true;
          vadSilenceFramesRef.current = 0;
          speechFramesCountRef.current++; // Track speech frames for minimum duration check
          console.log('[VAD] Speech detected - RMS:', rms.toFixed(4), 'Speech%:', (speechEnergyRatio * 100).toFixed(1));

          // Track strong speech for interruption
          if (isStrongSpeech) {
            strongSpeechFrames++;
            if (isPlayingAudioRef.current) {
              console.log(`[INTERRUPTION] Strong speech frame ${strongSpeechFrames} - RMS: ${rms.toFixed(4)} (threshold: ${INTERRUPTION_VOLUME_THRESHOLD.toFixed(4)}), Speech%: ${(speechEnergyRatio * 100).toFixed(1)}% (threshold: ${(INTERRUPTION_SPEECH_RATIO * 100).toFixed(1)}%)`);
            }
          } else {
            strongSpeechFrames = 0; // Reset if not strong speech
          }

          // Interruption detection: Require SUSTAINED strong speech (3+ frames = 150ms)
          // User must speak loudly for at least 150ms to interrupt
          const INTERRUPTION_FRAMES_REQUIRED = 3;
          if (strongSpeechFrames >= INTERRUPTION_FRAMES_REQUIRED && isPlayingAudioRef.current && isContinuousModeRef.current) {
            console.log(`🚫 User interruption detected (${strongSpeechFrames} frames of strong speech) - stopping TTS`);
            stopAllAudio();
            strongSpeechFrames = 0; // Reset after interruption
          }
        } else {
          strongSpeechFrames = 0; // Reset on silence
          // No speech (could be silence or background noise)
          if (frameCount > GRACE_PERIOD_FRAMES && hasDetectedSpeech) {
            vadSilenceFramesRef.current++;

            if (vadSilenceFramesRef.current % 20 === 0) {
              console.log('[VAD] No speech frames:', vadSilenceFramesRef.current, '/', SILENCE_DURATION_FRAMES, '(RMS:', rms.toFixed(4), 'Speech%:', (speechEnergyRatio * 100).toFixed(1) + ')');
            }

            if (vadSilenceFramesRef.current >= SILENCE_DURATION_FRAMES) {
              console.log('[VAD] 3.0 seconds without speech - checking if enough audio captured');

              // Safety check: Ensure we have at least 0.2 seconds of actual speech
              // At 50ms per frame check, 4 frames = 0.2 seconds
              const MIN_SPEECH_FRAMES = 4; // 0.2 seconds minimum (allows short phrases like "number two")
              const speechDurationSeconds = (speechFramesCountRef.current * 50) / 1000;

              if (speechFramesCountRef.current < MIN_SPEECH_FRAMES) {
                console.warn(`⚠️ Insufficient audio: only ${speechDurationSeconds.toFixed(2)}s of speech detected (min: 0.2s)`);
                console.log('🔄 Pausing recording - please speak louder or closer to microphone');

                // Reset counters but keep recording active
                vadSilenceFramesRef.current = 0;
                speechFramesCountRef.current = 0;
                hasDetectedSpeech = false;

                // TODO: Show visual feedback to user that they need to speak louder/closer
                // For now, just log and allow another attempt
              } else {
                console.log(`✅ Sufficient audio captured: ${speechDurationSeconds.toFixed(2)}s of speech`);
                stopVoiceRecording();

                // In continuous mode, DON'T restart automatically
                // Let the backend response (chat-response/tts-complete) trigger restart when needed
                // This prevents infinite recording loops when user is silent
                if (isContinuousModeRef.current) {
                  console.log('🔄 Continuous mode: Waiting for backend response before restart');
                  // Recording will restart when:
                  // 1. TTS starts playing (for interruption detection)
                  // 2. We receive a selection/confirmation prompt
                }
              }
            }
          }
        }
      }, 50); // Check every 50ms

      // Setup media recorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      console.log('📹 MediaRecorder created');

      // Capture current session ID in closure
      const currentSessionId = voiceSessionIdRef.current;

      mediaRecorder.ondataavailable = async (event) => {
        console.log('📦 Audio chunk available, size:', event.data.size, 'sessionId:', currentSessionId, 'current:', voiceSessionIdRef.current, 'isStopping:', isStoppingRef.current);

        // CRITICAL: Only send if this is still the active session
        if (currentSessionId !== voiceSessionIdRef.current) {
          console.log('⚠️ Ignoring chunk from old session:', currentSessionId);
          return;
        }

        // Once the final chunk has been emitted (and end-recording scheduled),
        // any further chunks are leftovers that arrive after the user stopped.
        // Drop them so we don't keep streaming audio to the gateway.
        if (finalChunkSentRef.current) {
          console.log('⚠️ Dropping post-stop chunk for session:', currentSessionId);
          return;
        }

        if (event.data.size > 0 && aiGatewayService.isConnected()) {
          const arrayBuffer = await event.data.arrayBuffer();
          console.log('📤 Sending audio chunk to backend...');

          aiGatewayService.assistantSendAudioChunk({
            audioData: arrayBuffer,
            sessionId: currentSessionId, // Use captured session ID, not ref
          });

          // If this is the final chunk (stopping), signal end-recording after sending
          if (isStoppingRef.current && !finalChunkSentRef.current) {
            console.log('✅ Final chunk sent, now signaling end-recording');
            finalChunkSentRef.current = true;

            // Give a small delay to ensure the chunk is processed
            setTimeout(() => {
              aiGatewayService.assistantEndRecording({
                sessionId: currentSessionId, // Use captured session ID
              });
            }, 100);
          }
        } else {
          console.warn('⚠️ Cannot send audio chunk - gateway not connected or empty data');
        }
      };

      mediaRecorder.onstop = () => {
        console.log('⏹️ MediaRecorder stopped for session:', currentSessionId);

        // Always release this recorder's stream tracks — the closure-bound
        // `stream` is the one this recorder was attached to, and skipping
        // the release on session-mismatch was the source of the lingering
        // mic indicator after rapid restart.
        stream.getTracks().forEach(track => {
          try { track.stop(); } catch { /* ignore */ }
        });
        if (mediaStreamRef.current === stream) {
          mediaStreamRef.current = null;
        }

        // Only process if this is still the active session
        if (currentSessionId !== voiceSessionIdRef.current) {
          console.log('⚠️ Ignoring onstop from old session:', currentSessionId);
          return;
        }

        // Set transcribing state - backend is now processing the audio
        setIsTranscribing(true);

        // Check if final chunk was sent (via ondataavailable)
        // If not, it means no data was available, so send end-recording now
        setTimeout(() => {
          if (!finalChunkSentRef.current) {
            console.log('📤 No final chunk received, sending end-recording now...');
            aiGatewayService.assistantEndRecording({
              sessionId: currentSessionId, // Use captured session ID
            });
          }

          // Reset flags
          isStoppingRef.current = false;
          finalChunkSentRef.current = false;

          // Auto-send the transcribed message after a short delay
          // to allow final transcription chunks to arrive
          console.log('⏰ Waiting 1.5 seconds for final transcription...');
          setTimeout(() => {
            // Use state variable instead of ref value, as state may not have synced to DOM yet
            // The voiceTranscript state is updated in real-time as transcription chunks arrive
            if (message.trim()) {
              console.log('📝 Transcribed text from state:', message);
              console.log('📤 Auto-sending message to AI...');
              handleSendMessage();
            } else {
              console.warn('⚠️ No transcribed text to send. State was empty.');
            }
          }, 1500); // Give 1.5 seconds for final transcription
        }, 200); // Wait 200ms for potential final ondataavailable
      };

      // Record in 1-second chunks for real-time streaming
      console.log('▶️ Starting MediaRecorder...');
      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      console.log('✅ Recording started successfully');

    } catch (err) {
      console.error('Failed to start voice recording:', err);
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsListening(false);
    }
  };

  const stopVoiceRecording = () => {
    console.log('🛑 stopVoiceRecording called');
    if (mediaRecorderRef.current && isRecordingRef.current) {
      console.log('⏹️ Stopping MediaRecorder...');

      // Set flag to indicate we're stopping
      isStoppingRef.current = true;
      finalChunkSentRef.current = false;

      // Request final data before stopping. This ensures ondataavailable fires
      // with the last chunk so the gateway receives it before end-recording.
      // When the recorder is somehow already inactive we skip the stop() call
      // but still release the mic below — the previous code path leaked the
      // stream entirely in that case.
      const recorder = mediaRecorderRef.current;
      // Tear down VAD eagerly regardless of recorder state — the analyser is
      // attached to the same MediaStream we are about to stop, and there's no
      // value in another 50ms read against a dying source.
      stopVadAnalysis();
      if (recorder.state === 'recording') {
        try { recorder.stop(); } catch (e) {
          console.warn('Failed to stop MediaRecorder cleanly:', e);
        }
      } else {
        // Recorder is no longer running, so onstop will not fire to release
        // the mic stream — release it now.
        releaseMicStream();
      }

      setIsRecording(false);
      isRecordingRef.current = false;
      setIsListening(false);
    } else {
      console.warn('⚠️ No active recording to stop');
      // Even with no active recorder, an orphaned stream could still be open
      // (e.g., recorder errored before reaching the running state). Release
      // unconditionally so the OS mic indicator drops.
      releaseMicStream();
    }
  };

  // Wake-word overlays (consent prompt + first-time download chip). These
  // must render regardless of whether the chat panel is open, because the
  // hook fires + the model downloads in the background. Positioned `fixed`
  // relative to the viewport so they don't depend on the bubble's
  // expanded-state container.
  const wakeWordOverlay = (
    <>
      <WakeWordOnboardingModal
        isOpen={showWakeOnboarding}
        onCancel={() => {
          setShowWakeOnboarding(false);
          updateVoiceSettings({ enableWakeWord: false });
        }}
        onConfirm={() => {
          // Don't close the modal yet — flip on consent so the worker
          // starts the download, but keep the modal mounted so it can
          // switch into the "downloading… please wait" phase. The
          // modal auto-dismisses itself after the brief "ready" beat
          // via onReadyDismiss below. Closing here would drop the
          // user back to the page with only a small corner chip,
          // which is easy to miss.
          setWakeWordConsented(true);
        }}
        // Once consented, the worker starts loading. Forward the
        // hook's progress signals so the modal can render its
        // "downloading…" phase. `isFreshDownload` gates this so a
        // cache hit (where the model is already local) doesn't show
        // the chip at all — the modal closes immediately on confirm.
        isDownloading={
          wakeWordConsented && isFreshDownload && wakeWordState.isModelLoading
        }
        downloadProgress={wakeWordState.loadingProgress}
        isReady={
          wakeWordConsented &&
          isFreshDownload &&
          !wakeWordState.isModelLoading &&
          wakeWordState.isListening
        }
        onReadyDismiss={() => {
          setShowWakeOnboarding(false);
          // Fresh-download window closes here, AFTER the ready beat — see
          // the notifiedReadyRef effect for the rationale.
          setIsFreshDownload(false);
        }}
        onMinimize={() => {
          setShowWakeOnboarding(false);
          if (wakeWordState.isModelLoading) {
            toast(
              'Continuing download in the background. We’ll notify when it’s ready.',
            );
          }
        }}
      />
      {/* Corner chip — only when the modal is NOT carrying the
          download UI (i.e. the user dismissed the modal but the
          worker is still finishing). Avoids two competing progress
          surfaces during a normal first-run download. */}
      {enableWakeWord && isFreshDownload && wakeWordState.isModelLoading && !showWakeOnboarding && (
        <div
          className="fixed bottom-24 end-6 z-50 px-3 py-2 rounded-xl shadow-lg flex flex-col gap-1 text-xs font-medium min-w-[220px]"
          style={{
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            color: 'white',
          }}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2">
            <Download className="w-3.5 h-3.5 animate-pulse" />
            <span className="flex-1">
              {`Downloading voice model… ${wakeWordState.loadingProgress ?? 0}%`}
            </span>
          </div>
          <div
            className="w-full h-1 rounded-full overflow-hidden"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}
          >
            <div
              className="h-full transition-[width] duration-200 ease-out"
              style={{
                width: `${wakeWordState.loadingProgress ?? 0}%`,
                backgroundColor: 'rgb(34, 197, 94)',
              }}
            />
          </div>
          <span className="text-[10px] opacity-75">
            One-time download per browser
          </span>
        </div>
      )}
    </>
  );

  // The launcher pill (MaieLauncherPill) is the entry point now — when the
  // panel is closed or minimized, only the wake-word overlay renders. The
  // pill takes over the bottom-right and re-opens the panel via
  // setShowAssistant(true).
  if (isMinimized || !showAssistant) return wakeWordOverlay;

  // Expanded chat interface
  return (
    <>
      {wakeWordOverlay}
    <div
      ref={bubbleRef}
      // flex flex-col is load-bearing: without it, children sit as
      // block-level boxes and the chip rail + input lose the
      // "messages absorbs remaining space" contract that overflow-y-auto
      // on the messages area depends on. When a long patient chip
      // rail or growing message list pushed total content past
      // height, the input got clipped by outer overflow-hidden.
      className={`fixed flex flex-col rounded-2xl shadow-2xl z-50 overflow-hidden ${isResizing ? 'select-none' : ''}`}
      style={{
        width: window.innerWidth < 640 ? 'calc(100vw - 2rem)' : `${size.width}px`,
        height: window.innerWidth < 640 ? 'auto' : `${size.height}px`,
        // Position based on bottom-right corner
        right: window.innerWidth < 640 ? '1rem' : `${Math.max(0, window.innerWidth - position.x)}px`,
        bottom: window.innerWidth < 640 ? '1rem' : `${Math.max(0, window.innerHeight - position.y)}px`,
        backgroundColor: 'var(--color-bg-primary)',
        maxHeight: window.innerWidth < 640 ? '70vh' : 'none',
        minWidth: '300px',
        minHeight: '200px',
        // Persistent border: bg-primary is pure black in the dark
        // themes, so without a stroke the bubble's edges vanish into
        // any dark page behind it — shadow alone doesn't read there.
        border: isResizing
          ? '2px solid var(--color-primary-600)'
          : '1px solid var(--color-border-secondary)',
        transition: isResizing ? 'none' : 'border 0.2s'
      }}
    >
      {/* Header */}
      <div
        className={`px-3 py-2.5 flex items-center justify-between gap-2 border-b flex-shrink-0 ${window.innerWidth >= 640 ? 'cursor-move' : ''}`}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)'
        }}
        onMouseDown={(e) => {
          if (window.innerWidth >= 640) {
            e.preventDefault();
            const rect = bubbleRef.current?.getBoundingClientRect();
            if (rect) {
              // Calculate offset from mouse to bottom-right corner
              setDragOffset({
                x: e.clientX - rect.right,
                y: e.clientY - rect.bottom
              });
              setIsDragging(true);
            }
          }
        }}
        onTouchStart={(e) => {
          if (window.innerWidth >= 640) {
            e.preventDefault();
            const rect = bubbleRef.current?.getBoundingClientRect();
            if (rect) {
              const touch = e.touches[0];
              // Calculate offset from touch to bottom-right corner
              setDragOffset({
                x: touch.clientX - rect.right,
                y: touch.clientY - rect.bottom
              });
              setIsDragging(true);
            }
          }
        }}
      >
        <div className="flex items-center gap-x-2.5 min-w-0">
          <div className="relative flex-shrink-0">
            <AssistantDisc active={isThinking || isStreaming} />
            {/* Subtle status dot — green when idle-and-ready, primary while
                a response is in flight. */}
            <span
              aria-hidden
              className="absolute -bottom-0.5 -end-0.5 w-2 h-2 rounded-full border"
              style={{
                backgroundColor:
                  isThinking || isStreaming
                    ? 'var(--color-primary-500)'
                    : 'var(--color-success)',
                borderColor: 'var(--color-bg-primary)',
              }}
            />
          </div>
          <span
            className="font-semibold text-sm truncate"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {branding.aiProvider || t('aiAssistant.brandFallback')}
          </span>
        </div>
        <div className="flex items-center gap-x-2">
          {/* Quick TTS mute. Stops in-flight playback if TTS was on, and
              flips the persistent voice-settings flag so future
              responses honour the choice. Shows a subtle dot when
              currently muted so the doctor knows responses won't speak. */}
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => {
              const next = !voiceSettings.enableTTS;
              updateVoiceSettings({ enableTTS: next });
              if (!next) {
                // Cut any audio that's already playing the moment the
                // user mutes — don't make them wait for the rest of a
                // sentence to finish.
                stopAllAudio();
              }
            }}
            className="relative"
            title={
              voiceSettings.enableTTS
                ? 'Mute spoken responses'
                : 'Enable spoken responses'
            }
            aria-pressed={!voiceSettings.enableTTS}
          >
            {voiceSettings.enableTTS ? <Volume2 /> : <VolumeX className="opacity-80" />}
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => {
              // Same thread, bigger room: the workspace deep-links the
              // bubble's active thread and shares its server state, so
              // this is a zero-handoff continuation, not a new chat.
              // The workspace sidebar is also the full conversation
              // list, so this doubles as "all chats".
              setShowAssistant(false);
              navigate(
                activeChatId
                  ? `/assistant?thread=${activeChatId}`
                  : '/assistant',
              );
            }}
            title="All chats"
          >
            <ExternalLink />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setShowAssistant(false)}
            title="Close"
          >
            <X />
          </Button>
        </div>
      </div>

      {/* Slash-command palette — when the user types `/` we replace the
          chat area with the command palette in place. Same fixed height
          as the chat area so the bubble's header + input stay rooted
          and nothing pushes around. The chat area below stays mounted
          (display:none) so scroll position is preserved on dismiss. */}
      {message.startsWith('/') &&
        !message.includes(' ') && (
          <div
            className="overflow-y-auto p-2"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              height: window.innerWidth < 640
                ? 'calc(70vh - 120px)'
                : `${size.height - 120}px`,
            }}
          >
            <SlashCommandMenu
              query={message}
              activeIndex={slashActiveIndex}
              setActiveIndex={setSlashActiveIndex}
              onSelect={(cmd) => {
                handleSlashCommand(cmd, '');
                messageInputRef.current?.focus();
              }}
              commands={SLASH_COMMANDS}
            />
          </div>
        )}

      {/* Chat Area — flex-1 so messages absorb leftover vertical
          space and scroll internally. Without flex-1 the area would
          grow to fit its content and push the chip rail + input
          past the bubble's bottom edge (which is clipped by outer
          overflow-hidden). */}
      <div
        ref={chatMessagesRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          display:
            message.startsWith('/') && !message.includes(' ') ? 'none' : undefined,
          height: window.innerWidth < 640
            ? 'calc(70vh - 120px)'
            : `${size.height - 120}px`
        }}
      >
        {/* Welcome Message */}
        {chatMessages.length === 0 && (
          <div className="flex items-start gap-x-3">
            <AssistantDisc className="mt-0.5" />
            <div className="flex-1 min-w-0 pt-1">
              <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                {t('aiAssistant.welcome.greeting')}
              </p>
              <ul className="mt-2 space-y-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                <li>• {t('aiAssistant.welcome.bullet.navigate')}</li>
                <li>• {t('aiAssistant.welcome.bullet.search')}</li>
                <li>• {t('aiAssistant.welcome.bullet.create')}</li>
                <li>• {t('aiAssistant.welcome.bullet.answer')}</li>
              </ul>
              <p className="mt-3 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                {t('aiAssistant.welcome.examples')}
              </p>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        {chatMessages.map((msg, idx) => (
          // Workspace design language: assistant turns get the gradient
          // avatar disc with prose flowing beside it (no heavy bubble);
          // user turns are a quiet end-aligned card on the selected tint.
          <div
            key={idx}
            className={`group flex items-start gap-x-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
          >
            {msg.role === 'assistant' && (
              <AssistantDisc
                className="mt-0.5"
                active={
                  msg.content === '' &&
                  idx === chatMessages.length - 1 &&
                  (isThinking || isStreaming)
                }
              />
            )}
            <div
              className={`space-y-2 min-w-0 ${
                msg.role === 'user'
                  ? 'flex flex-col items-end max-w-[85%]'
                  : 'flex-1 pt-0.5'
              }`}
            >
              {/* Per-turn patient indicator: when the user attached a
                  patient (chip or page-context) for this turn, surface it
                  inline on the message so the conversation makes sense
                  even after the chip rail clears. Renders only on user
                  messages — assistant responses inherit the same patient
                  via the thread binding, so duplicating it would be noise. */}
                {msg.role === 'user' && msg.patientContext && (
                <div
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
                  style={{
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary-500) 14%, transparent)',
                    color: 'var(--color-primary-700)',
                    border:
                      '1px solid color-mix(in srgb, var(--color-primary-500) 25%, transparent)',
                  }}
                  title={`This message is about ${msg.patientContext.name}`}
                >
                  <UserIcon className="w-3 h-3" />
                  <span className="truncate max-w-[180px]">
                    {msg.patientContext.name}
                  </span>
                </div>
              )}
              <div
                className={
                  msg.role === 'user'
                    ? 'rounded-2xl rounded-ee-md border px-3.5 py-2 max-w-full'
                    : undefined
                }
                style={
                  msg.role === 'user'
                    ? {
                        backgroundColor:
                          'var(--color-bg-selected, var(--color-bg-secondary))',
                        borderColor: 'var(--color-border-primary)',
                      }
                    : undefined
                }
              >
                {/* Last assistant turn shows typing dots while it's still
                    empty — avoids a blank row next to the avatar disc
                    during the pre-token window. */}
                {msg.role === 'assistant' &&
                msg.content === '' &&
                idx === chatMessages.length - 1 &&
                (isThinking || isStreaming) ? (
                  <div className="flex flex-col gap-1 py-0.5">
                    <div className="flex items-center gap-1" aria-label={t('aiAssistant.thinking')}>
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ backgroundColor: 'var(--color-text-tertiary)', animationDelay: '0ms' }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ backgroundColor: 'var(--color-text-tertiary)', animationDelay: '150ms' }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ backgroundColor: 'var(--color-text-tertiary)', animationDelay: '300ms' }}
                      />
                    </div>
                    {slowHint && (
                      <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                        {t('aiAssistant.takingLonger')}
                      </span>
                    )}
                  </div>
                ) : msg.role === 'assistant' ? (
                  // Assistant outputs are markdown-formatted (SOAP-style
                  // headings, bullets, bold call-outs). User messages
                  // stay plain so a stray `**` they type isn't rendered
                  // as bold. GFM enables tables + task lists.
                  // Citation markers ([E<n>], [S], [T:tool]) are walked
                  // out of every text-bearing element and replaced with
                  // CitationChip — the inline label sits as a superscript
                  // pill the user can click for the source. Walking
                  // happens AFTER markdown so the chip plays nice with
                  // bold/italic/list rendering.
                  <div className="flex items-start gap-1">
                  <div
                    className="text-sm break-words ai-chat-markdown min-w-0 flex-1"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={buildCitationMarkdownComponents({
                        encounterIdMap: msg.encounterIdMap,
                        patientId:
                          msg.patientContext?.id ??
                          currentPatientContext?.id ??
                          pageContext.currentPatient?.id ??
                          null,
                        onOpenEncounter: (encounterId) =>
                          executeActions([
                            {
                              type: 'navigate',
                              parameters: {
                                view: 'edit-record',
                                recordId: encounterId,
                                ...(msg.patientContext?.id
                                  ? { patientId: msg.patientContext.id }
                                  : currentPatientContext?.id
                                    ? { patientId: currentPatientContext.id }
                                    : pageContext.currentPatient?.id
                                      ? { patientId: pageContext.currentPatient.id }
                                      : {}),
                              },
                              description: 'Open cited encounter',
                            },
                          ]),
                        onOpenPatientSummary: (patientId) =>
                          executeActions([
                            {
                              type: 'navigate',
                              parameters: { view: 'patient', patientId },
                              description: 'Open patient profile (summary)',
                            },
                          ]),
                      })}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                  {/* Hover-reveal copy — workspace idiom. */}
                  {msg.content !== '' && <CopyMessageButton text={msg.content} />}
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-primary)' }}>
                    {msg.content}
                  </p>
                )}
              </div>

              {/* Show analysis results if present - using new AnalysisResultCard */}
              {Boolean(msg.analysisData) && msg.role === 'assistant' && (
                <AnalysisResultCard
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  analysis={msg.analysisData as any}
                  onScheduleFollowUp={() => {
                    // If we have current patient context, trigger appointment scheduling
                    if (currentPatientContext) {
                      executeActions([{
                        type: 'navigate',
                        parameters: {
                          view: 'add-appointment',
                          patientId: currentPatientContext.id,
                          patientName: currentPatientContext.name
                        },
                        description: `Schedule follow-up appointment for ${currentPatientContext.name}`
                      }]);
                    } else {
                      // No patient context, just navigate to schedule page
                      executeActions([{
                        type: 'navigate',
                        parameters: { view: 'schedule' },
                        description: 'Navigate to schedule page'
                      }]);
                    }
                  }}
                />
              )}

              {/* Show booking confirmation card for prepare_booking actions */}
              {msg.actions && msg.role === 'assistant' && msg.actions.some(a => a.type === 'prepare_booking') && (
                <div className="ps-3">
                  {msg.actions.filter(a => a.type === 'prepare_booking').map((action, actionIdx) => (
                    <BookingConfirmationCard
                      key={actionIdx}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      proposal={action.parameters as any}
                      onConfirmed={() => {
                        setChatMessages(prev => [
                          ...prev,
                          { role: 'assistant', content: 'Appointment has been booked successfully.' }
                        ]);
                      }}
                      onCancelled={() => {
                        setChatMessages(prev => [
                          ...prev,
                          { role: 'assistant', content: 'Booking cancelled.' }
                        ]);
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Show action badges if actions were executed */}
              {msg.actions && msg.actions.length > 0 && msg.role === 'assistant' && !msg.actions.some(a => a.type === 'prepare_booking') && (
                <div className="flex flex-wrap gap-2 ps-3">
                  {msg.actions.map((action, actionIdx) => (
                    <div
                      key={actionIdx}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                      style={{
                        backgroundColor: 'var(--color-success-light)',
                        color: 'var(--color-success-dark)'
                      }}
                    >
                      <CheckCircle className="w-3 h-3" />
                      <span>{action.type.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* NEW: Voice-based Selection Context (LLM-powered) */}
        {Boolean(activeSelectionContext) && (
          <div className="p-4 rounded-lg border-2"
            style={{
              backgroundColor: 'var(--color-info-light)',
              borderColor: 'var(--color-info)'
            }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-info)' }} />
              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                {(activeSelectionContext as Record<string, unknown>).message as string || 'Please select an option'}
              </h4>
            </div>
            <div className="space-y-2 mb-3">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(activeSelectionContext as any).options?.map((option: any, index: number) => (
                <button
                  key={option.id}
                  onClick={() => handleManualSelection(option.id)}
                  className="w-full text-start p-3 rounded-lg border hover:border-primary-600 transition-all"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border-primary)'
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-primary-600">#{index + 1}</span>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {option.label}
                        </p>
                      </div>
                      {option.description && (
                        <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                          {option.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              💡 You can say "number 1", "number 2", etc., or click an option above
            </p>
          </div>
        )}

        {/* Pending Actions Confirmation */}
        {pendingActions.length > 0 && (
          <div className="p-4 rounded-lg border-2"
            style={{
              backgroundColor: 'var(--color-warning-light)',
              borderColor: 'var(--color-warning)'
            }}>
            {/* Check if it's a selection action */}
            {(pendingActions[0]?.type === 'select_patient' ||
              pendingActions[0]?.type === 'select_patient_for_lab_orders' ||
              pendingActions[0]?.type === 'select_patient_for_appointments' ||
              pendingActions[0]?.type === 'select_patient_for_medical_records') ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Select Patient
                  </h4>
                </div>
                <div className="space-y-2 mb-3">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(pendingActions[0].parameters?.patients as any[] | undefined)?.map((patient: any) => (
                    <button
                      key={patient.id}
                      onClick={() => handleSelectPatient(patient.id, `${patient.firstName} ${patient.lastName}`, pendingActions[0].type)}
                      className="w-full text-start p-3 rounded-lg border hover:border-primary-600 transition-all"
                      style={{
                        backgroundColor: 'var(--color-bg-primary)',
                        borderColor: 'var(--color-border-primary)'
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                            {patient.dateOfBirth && !isNaN(new Date(patient.dateOfBirth).getTime()) ? `DOB: ${formatCalendarDate(patient.dateOfBirth)}` : ''}
                            {patient.phoneNumber ? ` • Phone: ${patient.phoneNumber}` : ''}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  block
                  onClick={handleCancelActions}
                >
                  Cancel
                </Button>
              </>
            ) : pendingActions[0]?.type === 'select_lab_order' ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Select Lab Order
                  </h4>
                </div>
                <div className="space-y-2 mb-3">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(pendingActions[0].parameters?.labOrders as any[] | undefined)?.map((order: any) => (
                    <button
                      key={order.id}
                      onClick={() => handleSelectLabOrder(
                        order.id,
                        pendingActions[0].parameters.patientId as string,
                        pendingActions[0].parameters.patientName as string
                      )}
                      className="w-full text-start p-3 rounded-lg border hover:border-primary-600 transition-all"
                      style={{
                        backgroundColor: 'var(--color-bg-primary)',
                        borderColor: 'var(--color-border-primary)'
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {order.orderDate ? formatDate(order.orderDate) : 'Date not set'} - {order.status}
                          </p>
                          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                            Priority: {order.priority} • {order.tests?.length || 0} test(s)
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  block
                  onClick={handleCancelActions}
                >
                  Cancel
                </Button>
              </>
            ) : pendingActions[0]?.type === 'select_appointment' ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Select Appointment
                  </h4>
                </div>
                <div className="space-y-2 mb-3">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(pendingActions[0].parameters?.appointments as any[] | undefined)?.map((appointment: any) => (
                    <button
                      key={appointment.id}
                      onClick={() => handleSelectAppointment(
                        appointment.id,
                        pendingActions[0].parameters.patientId as string,
                        pendingActions[0].parameters.patientName as string
                      )}
                      className="w-full text-start p-3 rounded-lg border hover:border-primary-600 transition-all"
                      style={{
                        backgroundColor: 'var(--color-bg-primary)',
                        borderColor: 'var(--color-border-primary)'
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {appointment.appointmentDate ? new Date(appointment.appointmentDate).toLocaleDateString() : 'Date not set'} {appointment.appointmentTime ? `at ${appointment.appointmentTime}` : ''}
                          </p>
                          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                            {appointment.appointmentType} • {appointment.status}
                            {appointment.reason ? ` • ${appointment.reason}` : ''}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  block
                  onClick={handleCancelActions}
                >
                  Cancel
                </Button>
              </>
            ) : pendingActions[0]?.type === 'select_medical_record' ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Select Medical Record
                  </h4>
                </div>
                <div className="space-y-2 mb-3">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(pendingActions[0].parameters?.medicalRecords as any[] | undefined)?.map((record: any) => (
                    <button
                      key={record.id}
                      onClick={() => handleSelectMedicalRecord(
                        record.id,
                        pendingActions[0].parameters.patientId as string,
                        pendingActions[0].parameters.patientName as string
                      )}
                      className="w-full text-start p-3 rounded-lg border hover:border-primary-600 transition-all"
                      style={{
                        backgroundColor: 'var(--color-bg-primary)',
                        borderColor: 'var(--color-border-primary)'
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {record.visitDate ? formatDate(record.visitDate) : 'Date not set'} - {record.type}
                          </p>
                          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                            Status: {record.status}
                            {record.chiefComplaint ? ` • ${record.chiefComplaint}` : ''}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  block
                  onClick={handleCancelActions}
                >
                  Cancel
                </Button>
              </>
            ) : pendingActions[0]?.type === 'propose_patient' ? (
              <PatientProposalCard
                action={pendingActions[0]}
                onDismiss={handleCancelActions}
              />
            ) : pendingActions[0]?.type === 'propose_lab_orders' ||
              pendingActions[0]?.type === 'propose_imaging_orders' ||
              pendingActions[0]?.type === 'propose_prescriptions' ? (
              <ProposalCard
                action={pendingActions[0]}
                onDismiss={handleCancelActions}
              />
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    Confirm Actions
                  </h4>
                </div>
                <div className="space-y-2 mb-3">
                  {pendingActions.map((action, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span style={{ color: 'var(--color-text-primary)' }}>
                        • {action.type.replace('_', ' ').toUpperCase()}
                      </span>
                      {action.description && (
                        <span style={{ color: 'var(--color-text-tertiary)' }}>
                          - {action.description}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleConfirmActions}
                    className="flex-1"
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelActions}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Bottom Stop button — only while streaming. The "thinking"
            animation now lives inside the empty assistant bubble, so the
            old spinning-Sparkles indicator is gone (the in-bubble dots
            convey the same state with less noise). */}
        {isStreaming && (
          <div className="flex items-center justify-center py-2">
            <Button
              variant="outline"
              size="xs"
              onClick={() => {
                aiGatewayService.chatInterrupt();
                setIsStreaming(false);
                setIsThinking(false);
              }}
              title="Stop generating"
            >
              <StopCircle />
              Stop
            </Button>
          </div>
        )}

        {/* Transcribing Indicator */}
        {isTranscribing && !isThinking && (
          <div className="flex items-center gap-x-2 justify-center py-4">
            <div className="flex gap-x-1">
              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--color-primary-600)', animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--color-primary-600)', animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--color-primary-600)', animationDelay: '300ms' }}></div>
            </div>
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Transcribing audio...</span>
          </div>
        )}
      </div>

      {/* Context chip rail — VSCode Copilot-sidebar style. Each chip is
          a TOGGLE: enabled (default) means the context (today: a patient)
          will be sent with the next turn; disabled (dimmed) means it
          won't, without removing the chip itself. Clicking the chip
          body toggles enabled/disabled. The X button removes the chip
          outright — used when the user has finished with this patient
          and wants a clean rail (rare; usually they just dim). */}
      {contextChips.length > 0 && (
        <div
          // flex-shrink-0 so the chip rail can't be squeezed when the
          // messages list is short and the column otherwise tries to
          // balance heights. max-h + overflow caps unbounded growth
          // when a user attaches a dozen patients/contexts at once.
          className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 border-t flex-shrink-0 max-h-32 overflow-y-auto"
          style={{
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)',
          }}
        >
          {contextChips.map((chip) => {
            const isEnabled = chip.enabled !== false;
            const titleSuffix = isEnabled
              ? ' — included in next turn (click to hide)'
              : ' — hidden from this conversation (click to include)';
            return (
              <div
                key={`${chip.type}:${chip.id}`}
                className="inline-flex items-center gap-1.5 ps-2 pe-1 py-1 rounded-full text-[11px] max-w-full transition-opacity"
                style={{
                  backgroundColor: isEnabled
                    ? 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)'
                    : 'transparent',
                  color: isEnabled
                    ? 'var(--color-primary-700)'
                    : 'var(--color-text-tertiary)',
                  border: isEnabled
                    ? '1px solid color-mix(in srgb, var(--color-primary-500) 22%, transparent)'
                    : '1px dashed var(--color-border-primary)',
                  opacity: isEnabled ? 1 : 0.55,
                }}
                title={(chip.meta ? `${chip.label} · ${chip.meta}` : chip.label) + titleSuffix}
              >
                <button
                  type="button"
                  onClick={() => toggleContextChipEnabled(chip.id)}
                  className="inline-flex items-center gap-1.5 truncate min-w-0 outline-none"
                  style={{
                    color: 'inherit',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  aria-pressed={isEnabled}
                  aria-label={`${isEnabled ? 'Hide' : 'Include'} ${chip.label}`}
                >
                  {chip.type === 'patient' && (
                    <UserIcon className="w-3 h-3 flex-shrink-0" />
                  )}
                  <span
                    className="truncate max-w-[160px]"
                    style={{
                      fontWeight: 600,
                      textDecoration: isEnabled ? 'none' : 'line-through',
                    }}
                  >
                    {chip.label}
                  </span>
                  {chip.meta && (
                    <span
                      className="truncate max-w-[120px]"
                      style={{ opacity: 0.7, fontWeight: 400 }}
                    >
                      · {chip.meta}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // If the chip points at the SAME patient whose page
                    // we're on, X-out means "don't use as context for
                    // this turn" — NOT "delete the chip permanently".
                    // Detaching would let the patient-page auto-attach
                    // effect re-add it on the very next render (because
                    // lastAutoAttachedPatientIdRef already matches),
                    // making the X feel broken. Toggling enabled to
                    // false leaves a dimmed chip the user can re-enable
                    // by clicking the body. Off-page chips (added via
                    // "Add to context" elsewhere) still detach fully.
                    const isPagePatient =
                      chip.type === 'patient' &&
                      pageContext.currentPatient?.id === chip.id;
                    if (isPagePatient && chip.enabled !== false) {
                      toggleContextChipEnabled(chip.id);
                    } else {
                      detachContextChip(chip.id);
                    }
                  }}
                  aria-label={
                    chip.type === 'patient' &&
                    pageContext.currentPatient?.id === chip.id &&
                    chip.enabled !== false
                      ? `Hide ${chip.label} from this turn`
                      : `Remove ${chip.label}`
                  }
                  title={
                    chip.type === 'patient' &&
                    pageContext.currentPatient?.id === chip.id &&
                    chip.enabled !== false
                      ? 'Hide from this conversation (click chip body to re-include)'
                      : 'Remove from this conversation'
                  }
                  className="ms-0.5 w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10 flex-shrink-0"
                  style={{ color: 'inherit' }}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Input Area — flex-shrink-0 so it stays anchored to the
          bottom regardless of how many chips, messages, or other
          siblings exist above it. */}
      <div
        className="p-3 border-t flex-shrink-0"
        style={{
          borderColor: 'var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
        }}
      >
        {/* AI not entitled → subscribe / degrade panel instead of the input.
            Past messages above stay readable. */}
        {!assistantEnabled ? (
          <div
            className="flex flex-col items-center gap-2 rounded-2xl border p-3 text-center"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border-primary)',
            }}
          >
            <div
              className="text-[12.5px] font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {assistantAffordance.upsell
                ? 'Ask MAIE is part of a paid plan'
                : 'Ask MAIE is temporarily unavailable'}
            </div>
            <div
              className="text-[11.5px] leading-4"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {assistantAffordance.upsell
                ? 'Your trial has ended — subscribe to keep chatting.'
                : (assistantAffordance.message ??
                  'You can still work manually.')}
            </div>
            {assistantAffordance.upsell && (
              <Button
                size="sm"
                onClick={() => {
                  setShowAssistant(false);
                  navigate('/admin/subscription');
                }}
              >
                Subscribe
              </Button>
            )}
          </div>
        ) : (
        <>
        {/* The reply hit the model's token cap and stopped mid-sentence.
            Offer to pick it up rather than making the doctor guess whether
            that was the whole answer — and rather than making them retype
            "carry on". Hidden while a turn is in flight. */}
        {lastTruncated && !isStreaming && !isThinking && (
          <button
            type="button"
            onClick={() => handleSendMessage('Continue from where you stopped.')}
            className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full border px-3 py-1.5 text-xs font-semibold"
            style={{
              color: 'var(--color-primary-700)',
              borderColor:
                'color-mix(in srgb, var(--color-primary-500) 40%, transparent)',
              backgroundColor:
                'color-mix(in srgb, var(--color-primary-500) 10%, transparent)',
            }}
          >
            {t('aiAssistant.continueAnswer')}
          </button>
        )}
        {/* Single elevated composer surface — mirrors the /assistant
            workspace: one rounded-2xl bordered card on bg-primary with a
            transparent input and the action buttons inside it. */}
        <div
          className="flex items-center gap-x-1 rounded-2xl border ps-1.5 pe-1.5 py-1 shadow-sm transition-shadow focus-within:shadow-md"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: pendingPrompt
              ? 'var(--color-primary-600)'
              : 'var(--color-border-primary)',
            boxShadow: pendingPrompt
              ? '0 0 0 1px var(--color-primary-600)'
              : undefined,
          }}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleVoiceInput}
            disabled={isThinking}
            className={`rounded-xl flex-shrink-0 ${isRecording ? 'animate-pulse motion-reduce:animate-none' : ''}`}
            title={isRecording ? 'Stop recording' : 'Start voice input'}
          >
            {isRecording ? (
              <StopCircle style={{ color: 'var(--color-error)' }} />
            ) : (
              <Mic />
            )}
          </Button>
          <TextInput
            ref={messageInputRef}
            type="text"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setSlashActiveIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              const slashOpen =
                message.startsWith('/') && !message.includes(' ');
              if (slashOpen) {
                const matches = SLASH_COMMANDS.filter((c) =>
                  !message.slice(1)
                    ? true
                    : c.name
                        .toLowerCase()
                        .startsWith(message.slice(1).toLowerCase()),
                ).sort((a, b) => {
                  if (Boolean(a.comingSoon) !== Boolean(b.comingSoon)) {
                    return a.comingSoon ? 1 : -1;
                  }
                  return a.name.localeCompare(b.name);
                });
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSlashActiveIndex(
                    (slashActiveIndex + 1) % Math.max(1, matches.length),
                  );
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSlashActiveIndex(
                    (slashActiveIndex - 1 + Math.max(1, matches.length)) %
                      Math.max(1, matches.length),
                  );
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setMessage('');
                  return;
                }
                if (e.key === 'Enter' && matches.length > 0) {
                  e.preventDefault();
                  const cmd =
                    matches[Math.min(slashActiveIndex, matches.length - 1)];
                  handleSlashCommand(cmd, '');
                  return;
                }
              }
              if (
                e.key === 'Enter' &&
                !isRecording &&
                !isThinking &&
                !isStreaming
              ) {
                handleSendMessage();
              }
            }}
            onFocus={() => {
              if (contexts.length > 0) {
                setShowContextPreview(true);
              }
            }}
            placeholder={
              isRecording
                ? t('aiAssistant.placeholder.recording')
                : isTranscribing
                  ? t('aiAssistant.placeholder.transcribing')
                  : t('aiAssistant.placeholder.idle')
            }
            disabled={isRecording || isTranscribing}
            // text-base on mobile: iOS Safari auto-zooms the page when a
            // focused input's font-size is under 16px.
            className="flex-1 min-h-[40px] border-0 px-2 focus:ring-0 focus:border-transparent text-base sm:text-sm"
            style={{
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              boxShadow: 'none',
              opacity: isRecording || isTranscribing ? 0.7 : 1
            }}
          />
          <Button
            size="icon-sm"
            className="rounded-xl flex-shrink-0"
            onClick={() => handleSendMessage()}
            disabled={
              !message.trim() ||
              isThinking ||
              isStreaming ||
              isRecording ||
              pendingActions.length > 0
            }
            title={
              pendingActions.length > 0
                ? t('aiAssistant.send.completePendingFirst')
                : isThinking || isStreaming
                  ? t('aiAssistant.send.waitForResponse')
                  : t('aiAssistant.send.sendMessage')
            }
            aria-label={t('aiAssistant.send.sendMessage')}
          >
            <Send />
          </Button>
        </div>
        </>
        )}
      </div>

      {/* Resize Handle - Top Left Corner */}
      {window.innerWidth >= 640 && (
        <div
          className="absolute top-12 left-0 w-8 h-8 cursor-nw-resize group"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsResizing(true);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsResizing(true);
          }}
          title="Drag to resize"
        >
          {/* Resize corner triangle */}
          <svg
            className="absolute top-0 left-0 w-full h-full opacity-50 group-hover:opacity-100 transition-opacity"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M0 0L16 0L0 16Z"
              fill="var(--color-border-primary)"
              className="group-hover:fill-current group-hover:text-primary-600"
            />
            <path
              d="M4 0L0 0L0 4"
              stroke="var(--color-bg-primary)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M8 0L0 0L0 8"
              stroke="var(--color-bg-primary)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M12 0L0 0L0 12"
              stroke="var(--color-bg-primary)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}

      {/* Wake Word Listening Indicator */}
      {enableWakeWord && !wakeWordState.isModelLoading && (wakeWordState.isListening || wakeWordState.isManuallyActivated) && (
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 text-xs font-medium animate-pulse"
          style={{
            backgroundColor: wakeWordState.isManuallyActivated
              ? 'rgba(59, 130, 246, 0.95)' // Blue for push-to-activate
              : 'rgba(34, 197, 94, 0.95)', // Green for always-listening
            color: 'white'
          }}
        >
          <Volume2 className="w-3.5 h-3.5" />
          <span>
            {wakeWordState.isManuallyActivated
              ? t('aiAssistant.listening.pushToActivate', { key: pushToActivateKey })
              : t('aiAssistant.listening.wakeWord', {
                  brand: branding.aiProvider || 'MAIE',
                })}
          </span>
        </div>
      )}
    </div>
    </>
  );
};

